import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useParams, useSearchParams } from 'react-router-dom';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type FetchStatus,
} from '@tanstack/react-query';
import {
  CROSS_FOLDER_SORTS,
  type ComposePrefill,
  type ConversationMessage,
  type ConversationSummary,
  type Draft,
  type MessageDetail,
  type MessageSortOrder,
  type MessageSummary,
  type RemoteMode,
  type SearchPage,
  type SearchScope,
} from '@/api';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import { STALE_TIMES, messageDetailStaleTime } from '@/api/queryClient';
import { queryKeys, type MessageListFilters } from '@/api/queryKeys';
import { accountColorVar, useAccountContext } from '@/app/accounts';
import { useAccountFolders, useFolderContext } from '@/app/folders';
import {
  defaultMailView,
  isPickedMailbox,
  useConversationView,
  useReadingPane,
} from '@/app/preferences';
import {
  COMPOSE_INTENT_NEW,
  COMPOSE_PARAM,
  COMPOSE_TO_PARAM,
  isPlausibleAddress,
} from '@/app/composeIntent';
import { useDrawerPanel } from '@/app/drawer';
import {
  bulkFailureMessage,
  showMailNotice,
  useArchiveMessagesMutation,
  useDeleteMessagesMutation,
  useMoveMessagesMutation,
  reportActionNotice,
  useNotSpamMutation,
  useReportSpamMutation,
  useSetFlagsMutation,
  useTransferMessagesMutation,
  undoPending,
} from '@/hooks/useMessageMutations';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import {
  AddToTaskDialog,
  BlockSenderDialog,
  ComposeDialog,
  LabelPicker,
  ShortcutSheet,
  SnoozeDialog,
} from '@/app/lazyUi';
import { EmailTaskChip } from '@/features/email-task/EmailTaskChip';
import { useEmailTaskSnackbar } from '@/features/email-task/EmailTaskSnackbar';
import { SaveSnackbar } from '@/features/nextcloud/save/SaveSnackbar';
import { SCHEDULED_PATH } from '@/features/scheduling/route';
import { invalidateSnoozed, useSchedulingSnackbar } from '@/features/scheduling/useSchedulingSnackbar';
import { rfcMessageIdOf, type EmailTaskTarget } from '@/features/email-task/emailTask';
import { taskReminderUrl } from '@/realtime/taskReminders';
import { UndoSnackbar } from '@/components/Snackbar';
import { useBackToClose } from '@/hooks/useBackToClose';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useAiAvailability } from '@/features/ai-assist/aiAssist';
import { useAiSummary } from '@/features/ai-summary';
import { useHandheld } from '@/hooks/useMediaQuery';
import { Fab } from '@/components/Fab';
import { FolderActionsButton } from '@/components/FolderTree';
import { MailboxTree } from '@/components/MailboxTree';
import { Icon } from '@/components/Icon';
import { PullIndicator } from '@/components/PullIndicator';
import { displayName, formatBytes, formatFullDate } from '@/utils/format';
import {
  Menu,
  MessageContextMenu,
  MOVE_TO_SUBMENU_ID,
  useCopyMessages,
  useMessageDownload,
  type MailboxFolders,
  type MenuAnchor,
} from '@/features/context-menu';
import { openComposeWindow } from '@/features/compose-window';
import { subscribeRestoredSend, takeRestoredSend } from '@/features/send-undo';
import { openMessageWindow, useCachePatchChannel } from '@/features/message-window';
import {
  arrivalIds,
  clearArrivals,
  clearStreamGap,
  useAccountStreamStatus,
  useArrivalCount,
  useStreamGap,
  useUnifiedArrivalCount,
} from '@/realtime';
import { MessageList, type MessageContextMenuRequest } from './MessageList';
import { ConnectionStatusLine, NotAvailableOffline } from '@/offline/OfflineNotices';
import { useMessageSort } from './useMessageSort';
import { ReadingPane } from './ReadingPane';
import { sameMessagePlaceholder } from './sameMessagePlaceholder';
import type { ComposeSeed } from './ComposeDialog';
import { draftSeed } from './draftSeed';
import { keepUnread } from './useMarkReadAfter';
import { mailtoSeed } from './mailtoSeed';
import { buildReplyQuote, htmlToText } from './composeHtml';
import { VacationBanner } from '@/features/vacation/VacationBanner';
import { SentCopiesBanner } from '@/features/sent-copies';
import { useReauthPrompt } from '@/features/credential-notice/reauthPrompt';
import { usePickMailbox } from '@/components/AccountSwitcher';
import { bulkMenuItems, SelectionBar } from './SelectionBar';
import { rowKey, rowKeyOf } from './rowKey';
import { usePaneFocus } from './usePaneFocus';
import {
  conversationHolds,
  expandConversations,
  isConversationRow,
  labelTargetIds,
  toConversationRow,
} from './conversationRows';
import {
  conversationSortFor,
  useConversationList,
  type UnifiedConversationPage,
} from './useConversationList';
import { ConversationPane, type ConversationPaneProps } from './ConversationPane';
import { useConversation } from '@/features/conversation/useConversation';
import { conversationKeys } from '@/features/conversation/queryKeys';
import { describeLaneFailure, useUnifiedMessages, type UnifiedPage } from './useUnifiedMessages';
import { SearchScopeBar } from './SearchScopeBar';
import type { Folder, Label } from '@/api/types';
import { HeaderLabelChips, LabelDot } from '@/features/labels/LabelChips';
import { labelQuery, labelsForKeywords } from '@/features/labels/keywords';
import { useApplyLabelsMutation } from '@/features/labels/useApplyLabels';
import { useLabelsByAccount } from '@/features/labels/useLabels';
import {
  clampCrossFolderSort,
  commonFolderOf,
  describeSearchGaps,
  hasInOperator,
  listSearchParams,
  readRememberedScope,
  rememberScope,
  resolveSearchScope,
  SCOPE_PARAM,
  TRASH_PARAM,
  withFilterTerms,
} from './searchScope';
import {
  DEFAULT_UNIFIED_SORT,
  UNIFIED_SORTS,
  unifiedSortsFrom,
} from './unifiedList';
import {
  accountChips as accountChipsFor,
  labelMessagePath,
  labelViewPath,
  UNIFIED_LABEL,
  UNIFIED_PATH,
  unifiedMessagePath,
} from './unifiedRoute';
import { deliveredTo, ownAddresses } from './fromAddress';
import { useFolderFocus } from '@/realtime/useFolderFocus';
import { closeMessageNotices } from '@/realtime/messageNotice';
import { registerDropHandler, rowsToMove, type DropMode } from '@/features/dnd/messageDrag';
import './mail-list.css';
import './mail-reading.css';
// mail-compose.css belongs to ComposeDialog, a lazy chunk, which imports it
// itself: here it put ~1.5 KB gzip of compose-only CSS on every first paint (#577).

const PAGE_SIZE = 50;

/** The orders a conversation list can be in (plan/16 §2). */
const CONVERSATION_SORTS: readonly MessageSortOrder[] = ['date-desc', 'date-asc'];

/**
 * What the view reads of the list query, whichever list is showing — the
 * messages, the merged messages or the conversations (ADR 0077). Its status,
 * never its pages: those are read from the list they belong to.
 */
interface ListStatus {
  isFetching: boolean;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  fetchStatus: FetchStatus;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  dataUpdatedAt: number;
  refetch: () => Promise<unknown>;
  fetchNextPage: () => Promise<unknown>;
}

/**
 * The remembered-sort scope of the merged list.
 *
 * Not an account id: the orders it can offer are the intersection of every
 * lane's, minus the ones that cannot be compared across servers (ADR 0056 §2),
 * so remembering it under one account's key would let a folder view's arrival
 * order leak into a list that cannot perform it.
 */
const UNIFIED_SORT_SCOPE = 'unified';

/**
 * `location.state` on a history entry this view pushed (ADR 0048 §4).
 *
 * `from: 'list'` marks a message opened from the list, so ← can be the same
 * `navigate(-1)` the hardware back button is. `composePushed` marks a composer
 * opened by pushing `?compose=`, so closing it pops that entry rather than
 * leaving a copy of the list behind it in the history.
 */
interface MailLocationState {
  from?: 'list';
  composePushed?: boolean;
  /**
   * Handheld selection mode. Entering it pushes an entry for the same URL
   * carrying this, so the hardware back button leaves selection mode rather
   * than the folder; leaving it any other way pops that entry again.
   */
  selecting?: boolean;
}

function mailState(state: unknown): MailLocationState {
  return typeof state === 'object' && state !== null ? (state as MailLocationState) : {};
}

/** What the context menu is open on, and where it is anchored. */
interface MenuState {
  message: MessageSummary;
  anchor: MessageContextMenuRequest['anchor'];
  /** `V` opens the Move-to submenu detached on the cursor row. */
  openMoveTo: boolean;
  /** The grid container, captured when the menu opened. Never a row. */
  returnFocusTo: HTMLElement | null;
}

/**
 * The multi-select selection (doc 06 §5): ids, never indices, so a page
 * append, a live update or a re-sort cannot move it onto other mail. It is
 * stamped with the list it was made in; a different folder, filter or search
 * is a different list, and the selection simply does not apply there — no
 * effect has to remember to clear it.
 */
interface Selection {
  list: string;
  ids: ReadonlySet<string>;
  /** Where Shift-click ranges from: the last row toggled. */
  anchorId?: string;
}

const NO_SELECTION: Selection = { list: '', ids: new Set() };

/** Who the Block sender dialog is about, and where it was asked from. */
interface BlockTarget {
  accountId: string;
  address: string;
  /** The folder the message is in, so the server also moves mail from there. */
  folderId: string | undefined;
}

/**
 * How far from the top still counts as "parked at the top".
 *
 * Not zero: a trackpad, a rubber-band bounce or a restored scroll position
 * leaves a pixel or two, and a list that is visually at the top should catch up
 * quietly rather than wait behind a pill for the sake of 1px.
 */
const CATCH_UP_SCROLL_EPSILON_PX = 4;

/** The bulk actions menu: a sheet from the FAB, a popover on desktop. */
interface BulkMenuState {
  anchor: MenuAnchor;
  returnFocusTo: HTMLElement | null;
  /** `V` with a selection opens its Move-to submenu straight away. */
  openMoveTo?: boolean;
}

/**
 * The mail view: list pane + reading pane, and the owner of the mail keymap.
 *
 * Route shape: /mail/:folderId?/:messageId? — folder and open message both live
 * in the URL so every state is linkable and the back button behaves.
 *
 * Server state is TanStack Query throughout: the list is a cursor-paged
 * `useInfiniteQuery` keyed by account + filters, and every action is an
 * optimistic mutation from `useMessageMutations`.
 */
export function MailView({ unified = false }: { unified?: boolean } = {}) {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const locationState = mailState(location.state);
  const handheld = useHandheld();
  /**
   * Settings → General → Reading pane, on desktop and tablet widths. A phone
   * is one pane at a time whatever it says (ADR 0048), so the attribute is
   * left off there and the handheld rules alone decide the layout.
   */
  const [readingPaneSetting] = useReadingPane();
  const readingPane = handheld ? undefined : readingPaneSetting;
  const queryClient = useQueryClient();
  const {
    folderId: folderIdParam,
    messageId,
    accountId: messageAccountParam,
    labelId: labelIdParam,
    labelAccountId,
  } = useParams<{
    folderId?: string;
    messageId?: string;
    accountId?: string;
    labelId?: string;
    labelAccountId?: string;
  }>();
  const { accounts, activeAccountId, activeAccount, setActiveAccountId } = useAccountContext();
  /*
   * A label's view (plan/17 §5): every folder of the label's mailbox, through
   * `GET /search?scope=all&q=label:"<name>"`. The label belongs to the account
   * in the path, and the view reads it as the active one — the same move the
   * sidebar makes when a folder of another mailbox is picked.
   */
  const labelMode = !unified && labelIdParam !== undefined;
  useEffect(() => {
    if (!labelMode || labelAccountId === undefined || labelAccountId === activeAccountId) return;
    if (accounts.some((account) => account.id === labelAccountId)) setActiveAccountId(labelAccountId);
  }, [accounts, activeAccountId, labelAccountId, labelMode, setActiveAccountId]);
  const { folders, inbox, byId, folderByRole, isLoading: foldersLoading, error: foldersError } = useFolderContext();

  const folderId = unified ? undefined : (folderIdParam ?? inbox?.id);
  const folder = folderId ? byId.get(folderId) : undefined;
  // The open non-INBOX folder is polled faster on the server while this tab
  // is visible and in use (#67).
  useFolderFocus(labelMode ? undefined : activeAccountId, folder?.id, folder?.role === 'inbox');

  /* ------------------------------------------------- the unified view (ADR 0056) */

  /**
   * The mailboxes the merged list draws from, and the folders of each.
   *
   * In the ordinary view this is just the active account, and the query below
   * reads through the same key `FolderProvider` already populated — so the
   * role-folder resolver is one function in both modes and there is no second
   * fetch.
   */
  const laneAccountIds = useMemo(
    () => (unified ? accounts.map((account) => account.id) : activeAccountId ? [activeAccountId] : []),
    [accounts, activeAccountId, unified],
  );
  const accountFolders = useAccountFolders(laneAccountIds);
  const laneAccounts = useMemo(
    () => accounts.filter((account) => laneAccountIds.includes(account.id)),
    [accounts, laneAccountIds],
  );

  /*
   * Each lane's labels (plan/17 §5): one list per mailbox, never merged. A
   * row's chips come from ITS account's list, which is also the only list the
   * picker offers for it.
   */
  const labelsByAccount = useLabelsByAccount(laneAccountIds);
  const labelsOf = useCallback(
    (message: MessageSummary): readonly Label[] =>
      labelsForKeywords(message.labelKeywords, labelsByAccount.get(message.accountId || (activeAccountId ?? ''))),
    [activeAccountId, labelsByAccount],
  );
  const labelList = labelMode && labelAccountId === activeAccountId
    ? labelsByAccount.get(labelAccountId ?? '')
    : undefined;
  const currentLabel = labelMode
    ? labelList?.find((label) => label.id === labelIdParam && label.state !== 'deleting')
    : undefined;
  /** The list has answered and this label is not in it (deleted, or a stale link). */
  const labelMissing = labelMode && labelList !== undefined && currentLabel === undefined;

  /**
   * Archive and Trash, per account (ADR 0056 §4): `e` and `#` on a row act
   * inside *that row's* mailbox, not inside the one the view was built with.
   */
  const roleFolderOf = useCallback(
    (role: 'archive' | 'trash' | 'junk') => (accountId: string) =>
      accountFolders.get(accountId)?.byRole(role)?.id,
    [accountFolders],
  );
  /** Where Not spam sends a row: its own mailbox's Inbox. */
  const inboxOf = useCallback(
    (accountId: string) => accountFolders.get(accountId)?.inbox?.id,
    [accountFolders],
  );

  /**
   * Whether Archive can be offered for these rows.
   *
   * `POST …/messages/archive` is a 404 (`reason: no_archive_folder`) on a
   * mailbox that has no Archive folder, and the optimistic patch has no
   * destination to move the row to — so it removes it outright. Offered, that
   * is a row that leaves the list, a snackbar that says "Message archived",
   * and a message still sitting in the Inbox.
   *
   * The affordance is therefore withheld rather than made to create the folder
   * on the spot. The backend takes the same position everywhere the question
   * comes up ("no folder is created tonight"; a missing Drafts folder is a 404
   * naming the reason), and it is the right one here: `POST /folders` cannot
   * set the `\Archive` special-use flag, so a folder made behind this button
   * would carry the role only through our own name heuristic — a folder in a
   * mailbox the user's other clients also read, created by a one-key shortcut.
   * Making a folder is a deliberate act and already has a home in the folder
   * tree; once an Archive folder exists, by any route, this comes back by
   * itself.
   *
   * Per row, not per view: in the merged list `e` acts inside the row's own
   * mailbox (ADR 0056 §4), and two of them may not agree.
   *
   * Withheld only on *positive* knowledge. A tree that has not answered yet is
   * not evidence that the folder is missing, and a button that vanishes and
   * comes back is its own small lie.
   */
  const canArchive = useCallback(
    (targets: readonly MessageSummary[]) =>
      targets.length > 0 &&
      targets.every((target) => {
        // The row's own mailbox when this view has its tree — every lane of
        // the merged list does — and otherwise the view's.
        const tree =
          accountFolders.get(target.accountId) ?? accountFolders.get(activeAccountId ?? '');
        if (!tree || tree.isPending) return true;
        return tree.byRole('archive') !== undefined;
      }),
    [accountFolders, activeAccountId],
  );

  /**
   * Report spam vs Not spam, per row (ADR 0056 §4 again: the row's own
   * mailbox's Junk).
   *
   * - **Not spam** when every row is already in its Junk folder — the verb
   *   that takes it out again. A mixed selection gets Report spam, which is
   *   sent for the rows that are not in Junk yet.
   * - **Report spam** otherwise, withheld on the same positive knowledge as
   *   Archive: a mailbox with no Junk folder answers 404 `no_junk_folder`, so
   *   the verb is absent there rather than a row that leaves and comes back.
   */
  const junkOf = useCallback(
    (target: MessageSummary) =>
      (accountFolders.get(target.accountId) ?? accountFolders.get(activeAccountId ?? ''))?.byRole(
        'junk',
      )?.id,
    [accountFolders, activeAccountId],
  );
  const spamVerbFor = useCallback(
    (targets: readonly MessageSummary[]): 'report' | 'not-spam' | undefined => {
      if (targets.length === 0) return undefined;
      if (targets.every((target) => junkOf(target) === target.folderId)) return 'not-spam';
      const canReport = targets.every((target) => {
        const tree =
          accountFolders.get(target.accountId) ?? accountFolders.get(activeAccountId ?? '');
        if (!tree || tree.isPending) return true;
        return tree.byRole('junk') !== undefined;
      });
      return canReport ? 'report' : undefined;
    },
    [accountFolders, activeAccountId, junkOf],
  );

  /** One lane per mailbox that has an Inbox to read. */
  const unifiedInboxes = useMemo(
    () =>
      laneAccountIds.flatMap((accountId) => {
        const id = accountFolders.get(accountId)?.inbox?.id;
        return id ? [{ accountId, folderId: id }] : [];
      }),
    [accountFolders, laneAccountIds],
  );

  /** Which mailbox a row is from, as text plus a tint (ADR 0056 §8). */
  const accountChips = useMemo(
    () => (unified ? accountChipsFor(accounts, accountColorVar) : undefined),
    [accounts, unified],
  );

  /**
   * The account the reading pane, the composer and the per-account rules
   * belong to: the open message's in the unified view, the active one
   * otherwise. #256's "a composer is pinned to the account it was opened in"
   * then holds unchanged, because this is what it is pinned to.
   */
  const paneAccountId = unified ? (messageAccountParam ?? activeAccountId) : activeAccountId;

  /*
   * Settings → General's "Mail opens on" (ADR 0056 §7).
   *
   * Only bare `/mail` — a folder in the URL is a place someone asked for, and
   * redirecting away from it would make every link into a folder unreliable.
   * `replace`, so the back button does not bounce between the two lists. The
   * setting can only take effect when there is more than one mailbox to unify.
   */
  const bareMailRoute = !unified && folderIdParam === undefined && messageId === undefined;
  useEffect(() => {
    if (!bareMailRoute || accounts.length < 2) return;
    // The one navigation this must not touch: a mailbox somebody just picked
    // out of the switcher, which lands here precisely to LEAVE the merged list
    // (see `PICKED_MAILBOX_STATE`).
    if (isPickedMailbox(location.state)) return;
    if (defaultMailView() !== 'unified') return;
    navigate(UNIFIED_PATH, { replace: true });
  }, [accounts.length, bareMailRoute, location.state, navigate]);

  // Search lives in the URL (?q=) and is driven by the top bar, which owns the
  // "/" shortcut — one search box for the whole app.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('q') ?? '';
  const [filter, setFilter] = useState<'all' | 'unread' | 'flagged'>('all');
  const [compose, setCompose] = useState<ComposeSeed | undefined>(undefined);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const { available: aiAvailable } = useAiAvailability();
  const [menu, setMenu] = useState<MenuState | undefined>(undefined);
  const [bulkMenu, setBulkMenu] = useState<BulkMenuState | undefined>(undefined);
  /*
   * Every mailbox's folders for the Move to / Copy to lists (ADR 0108),
   * fetched only while one of those menus is open and only with more than
   * one mailbox. The keys are the folder tree's own, so with the sidebar
   * showing every mailbox this reads cached data and fetches nothing.
   */
  const pickerAccountIds = useMemo(
    () => ((menu !== undefined || bulkMenu !== undefined) && accounts.length > 1 ? accounts.map((a) => a.id) : []),
    [accounts, bulkMenu, menu],
  );
  const pickerFolders = useAccountFolders(pickerAccountIds);
  const pickerMailboxes = useMemo<MailboxFolders[] | undefined>(
    () =>
      pickerAccountIds.length > 1
        ? accounts.map((account) => ({
            accountId: account.id,
            label: account.email,
            folders: pickerFolders.get(account.id)?.folders ?? [],
          }))
        : undefined,
    [accounts, pickerAccountIds.length, pickerFolders],
  );
  const online = useOnlineStatus();
  const [selection, setSelection] = useState<Selection>(NO_SELECTION);
  const [blockTarget, setBlockTarget] = useState<BlockTarget | undefined>(undefined);
  /** The emails the "Add to Tasks" form is open for (ADR 0075). */
  const [taskTargets, setTaskTargets] = useState<EmailTaskTarget[] | undefined>(undefined);
  const emailTaskSnackbar = useEmailTaskSnackbar();
  /** The rows the Snooze chooser is open for (ADR 0076). */
  const [snoozeTargets, setSnoozeTargets] = useState<MessageSummary[] | undefined>(undefined);
  /** The rows the "Label as" picker is open for (plan/17), as rows: not expanded. */
  const [labelTargets, setLabelTargets] = useState<MessageSummary[] | undefined>(undefined);
  /** "Snoozed until …" and "Scheduled for …", each with its Undo. */
  const schedulingSnackbar = useSchedulingSnackbar();
  /** Said out loud when a popup blocker turned a double-click into nothing. */
  const [windowNotice, setWindowNotice] = useState<string | undefined>(undefined);
  /**
   * The remote-content mode asked for on the open message (ADR 0018).
   *
   * Keyed by message id so it resets itself when a different message is opened,
   * with no effect and no stale state — the same shape as the reading pane's
   * plain-text toggle. `undefined` means "let the persisted rules decide".
   */
  const [remoteChoice, setRemoteChoice] = useState<
    { messageId: string; mode: RemoteMode } | undefined
  >(undefined);

  /**
   * The grid container. The context menu hands focus back to *this* element and
   * never to a row: a row can be virtualized away while the menu is open, and
   * focus on a removed element falls to `<body>`, killing every later keystroke.
   */
  const gridRef = useRef<HTMLDivElement>(null);
  /** The two panes' sections: where pane cycling and focus return land (#205). */
  const listPaneRef = useRef<HTMLElement>(null);
  const readingPaneRef = useRef<HTMLElement>(null);
  /** A row asked to open, so its arrival may take focus (see `usePaneFocus`). */
  const openFromListRef = useRef(false);

  /**
   * Two of our windows, no SSE: every window broadcasts the `CachePatch[]` it
   * applies and applies what it receives (ADR 0020 §3.3), so a flag changed in
   * a message popup lands in this list without a refetch.
   */
  useCachePatchChannel();

  /**
   * The composer has two ways to be open, and this is the union of them.
   *
   * `c`, Reply and Forward seed it locally; the shell's Compose button renders
   * *above* this view and cannot reach that state, so it asks through
   * `?compose=` instead (see `@/app/composeIntent`). The two are combined by
   * derivation rather than by syncing the parameter into state in an effect —
   * an effect would open the dialog one render late and give the URL and the
   * state two chances to disagree. A local seed wins, so pressing Compose with
   * a reply already on screen cannot blank that draft.
   */
  const composeIntent = searchParams.get(COMPOSE_PARAM);
  // A recipient may ride along (Contacts' "Email"). Checked here rather than
  // trusted: this value came from a URL, and a URL is not ours.
  const composeTo = searchParams.get(COMPOSE_TO_PARAM);
  const seededTo = composeTo !== null && isPlausibleAddress(composeTo) ? composeTo.trim() : undefined;
  const composeSeed: ComposeSeed | undefined =
    compose ??
    (composeIntent !== null
      ? { mode: 'new', ...(seededTo === undefined ? {} : { to: seededTo }) }
      : undefined);

  /** Closing retracts *both* sources; leaving the parameter set would reopen it. */
  const closeCompose = () => {
    setCompose(undefined);
    setComposeProblem(undefined);
    if (composeIntent === null) return;
    // Opened by the handheld FAB, which pushed an entry so that the hardware
    // back button closes the composer: closing it any other way pops the same
    // entry, so back afterwards leaves the folder instead of revisiting it.
    if (locationState.composePushed) {
      navigate(-1);
      return;
    }
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete(COMPOSE_PARAM);
        next.delete(COMPOSE_TO_PARAM);
        return next;
      },
      // `replace`: the composer is not a place in the history the back button
      // should have to walk through on the way out of a folder.
      { replace: true },
    );
  };

  /**
   * Pop the composer out into its own window, draft and all.
   *
   * The in-page composer is closed the moment the window is open — both halves
   * would otherwise hold the same half-written message, and the copy left
   * behind here would still be sitting there after the window sent it. A
   * blocked popup changes nothing: the draft stays exactly where it is and the
   * live region says why, rather than the message vanishing into a window that
   * never appeared.
   */
  const popOutCompose = (draft: ComposeSeed) => {
    const result = openComposeWindow({ accountId: composeAccount?.id ?? paneAccountId ?? '', draft });
    if (!result.opened) {
      setWindowNotice('Your browser blocked the compose window; the draft is still here.');
      return;
    }
    setWindowNotice(undefined);
    closeCompose();
  };

  /**
   * Search is the folder listing with IMAP SEARCH behind it: same query, same
   * cursor, same page size — only the criteria change. It reaches the whole
   * folder on the server, not the page the client happens to have loaded.
   *
   * Debounced because the top bar rewrites `?q=` on every keystroke and each
   * distinct value costs a UID SEARCH on the mail host.
   */
  const searching = search.trim() !== '';
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim());
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  // The chosen order, remembered per account and already clamped to what this
  // account's server can actually do. The merged list remembers its own,
  // because the orders it can offer are not one account's (ADR 0056 §2).
  const { sort: storedSort, setSort, rememberSortsAvailable } = useMessageSort(
    unified ? UNIFIED_SORT_SCOPE : activeAccountId,
  );
  /*
   * Arrival order is not mergeable — a UID on one server means nothing on
   * another — so the merged list falls back to the `Date:` header, which is
   * also its displayed column (HANDOFF §5). Clamped here rather than refused,
   * so a remembered arrival order from a folder view cannot blank the list.
   */
  const viewSort =
    unified && !UNIFIED_SORTS.includes(storedSort) ? DEFAULT_UNIFIED_SORT : storedSort;

  /*
   * Where the search looks (Gmail's "search all mail"). The scope rides in the
   * URL beside `?q=` (see `searchScope.ts`); when it does not, the session's
   * last choice, and before that the Inbox searches everything while any other
   * folder searches itself. `in:` typed in the query overrides all of it — the
   * query then names its own folders, and only the search endpoint reads them.
   */
  const scopeParam = searchParams.get(SCOPE_PARAM);
  const rememberedScope = readRememberedScope();
  const scope = resolveSearchScope(scopeParam, unified ? undefined : folder?.role, rememberedScope);
  /*
   * The default depends on WHICH folder the search started in, and the folder
   * tree may not have answered yet. Guessing "Inbox" until it does would send a
   * search of every folder from Receipts and then a second, different one — so
   * nothing is fetched until the default can be decided.
   */
  const scopePending =
    !unified && scopeParam === null && rememberedScope === undefined && folder === undefined;
  const includeTrashJunk = searchParams.get(TRASH_PARAM) === '1';
  const inOperator = hasInOperator(debouncedSearch);
  /*
   * In a label's view the list IS a search: `label:"<name>"`, plus whatever
   * was typed into the box, across every folder (plan/17 §4).
   */
  const listSearch = labelMode
    ? [currentLabel ? labelQuery(currentLabel) : '', debouncedSearch].filter((part) => part !== '').join(' ')
    : debouncedSearch;
  const crossFolder = labelMode
    ? currentLabel !== undefined
    : debouncedSearch !== '' && (inOperator || scope === 'all');
  // A cross-folder page is one merge of many folders, and the server can merge
  // only on the dates and the size; anything else is sent as the date order.
  const sort = crossFolder ? clampCrossFolderSort(viewSort) : viewSort;

  /*
   * Conversation view (ADR 0077, plan/16 §8). The list groups by conversation
   * when the setting is on, nothing is being searched (results are messages),
   * the folder is not Drafts (a draft is edited, not read in a thread), and
   * the order is by date — arrival maps to date here, the key a conversation
   * is placed by. A sender, subject or size order shows single messages.
   *
   * Off — or not applicable — the flat list below is the code it always was:
   * same queries, same requests. The one new thing it waits for is the setting
   * itself (and, with the setting on, which folder this is): guessing and
   * correcting would fetch both lists and swap one for the other on screen.
   */
  const conversationSetting = useConversationView();
  const conversationSort = conversationSortFor(viewSort);
  const conversationEligible = !searching && !labelMode && conversationSort !== undefined;
  // Drafts is decided by the folder's role, so the tree has to have answered
  // (or given up: a tree that cannot load is not evidence of Drafts).
  const folderRoleKnown = unified || folder !== undefined || !foldersLoading;
  const conversationMode =
    conversationEligible &&
    conversationSetting === true &&
    folderRoleKnown &&
    (unified || folder?.role !== 'drafts');
  const listUndecided =
    conversationEligible &&
    (conversationSetting === undefined || (conversationSetting && !folderRoleKnown));
  const flatListOn = !conversationMode && !listUndecided;

  const setScope = useCallback(
    (next: SearchScope) => {
      rememberScope(next);
      setSearchParams(
        (params) => {
          const out = new URLSearchParams(params);
          out.set(SCOPE_PARAM, next);
          return out;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setIncludeTrashJunk = useCallback(
    (include: boolean) => {
      setSearchParams(
        (params) => {
          const out = new URLSearchParams(params);
          if (include) out.set(TRASH_PARAM, '1');
          else out.delete(TRASH_PARAM);
          // The toggle belongs to the All folders scope; turning it on is a
          // choice of that scope too, so the URL says so.
          out.set(SCOPE_PARAM, 'all');
          return out;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * The identity of this list. It is the query key's filter object, so a
   * folder/search/filter change *is* a different query: pages, scroll position
   * and the "load more" cursor all belong to the key rather than to component
   * state, which is what removes the old reset-and-accumulate effects.
   *
   * `sort` IS a member: a cursor is a position in one particular ordered
   * answer, and the backend refuses a cursor paired with a different order, so
   * changing the sort has to start a new list from page one.
   */
  const filters: MessageListFilters = useMemo(
    () => ({
      // A cross-folder search does not depend on where it started: opening a
      // result from Sent moves the route to Sent and must keep this list.
      folderId: crossFolder ? '' : (folderId ?? ''),
      sort,
      ...(filter === 'unread' ? { unreadOnly: true } : {}),
      ...(filter === 'flagged' ? { flaggedOnly: true } : {}),
      ...(listSearch !== '' ? { search: listSearch } : {}),
      ...(crossFolder ? { scope: inOperator ? ('in' as const) : ('all' as const) } : {}),
      ...(crossFolder && !inOperator && includeTrashJunk ? { includeTrashJunk: true } : {}),
    }),
    [crossFolder, folderId, filter, listSearch, sort, inOperator, includeTrashJunk],
  );

  // Memoized: the key is a dependency of the recovery effect below, and a
  // fresh array on every render would re-run it on every render.
  const listKey = useMemo(
    () => queryKeys.account(activeAccountId ?? '').messages(filters),
    [activeAccountId, filters],
  );

  /*
   * The merged list (ADR 0056). Both queries are always constructed — a hook
   * cannot be conditional — and exactly one is `enabled`; the other costs a
   * disabled observer and nothing else.
   */
  const unifiedFilters = useMemo(
    () => ({
      ...(debouncedSearch !== '' ? { search: debouncedSearch } : {}),
      ...(crossFolder ? { allFolders: true } : {}),
      ...(crossFolder && includeTrashJunk ? { includeTrashJunk: true } : {}),
      ...(filter === 'unread' ? { unreadOnly: true } : {}),
      ...(filter === 'flagged' ? { flaggedOnly: true } : {}),
    }),
    [crossFolder, debouncedSearch, filter, includeTrashJunk],
  );
  const { query: unifiedQuery, queryKey: unifiedKey } = useUnifiedMessages({
    inboxes: unifiedInboxes,
    filters: unifiedFilters,
    sort,
    enabled: unified && (!searching || debouncedSearch !== '') && flatListOn,
  });

  const folderQuery = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam, signal }): Promise<SearchPage> =>
      crossFolder
        ? /*
           * Every folder at once (or the ones the query's `in:` named): the
           * search endpoint, whose cursor interleaves the folders on the
           * server. The Unread / Starred chips become the query's own `is:`
           * terms, because that endpoint takes one query line.
           */
          api.messages.search(
            activeAccountId ?? '',
            {
              query: withFilterTerms(listSearch, filter),
              scope: 'all',
              ...(filters.includeTrashJunk ? { includeTrashJunk: true } : {}),
              sort,
              ...(pageParam ? { cursor: pageParam } : {}),
              limit: PAGE_SIZE,
            },
            signal,
          )
        : api.messages.list(
            {
              accountId: activeAccountId ?? '',
              ...filters,
              ...(pageParam ? { cursor: pageParam } : {}),
              limit: PAGE_SIZE,
            },
            signal,
          ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // Nothing fetches between the keystroke and the debounce: the unfiltered
    // folder is not what was asked for, and showing it would flash the wrong
    // list on every character typed.
    enabled:
      !unified &&
      activeAccountId !== undefined &&
      (folderId !== undefined || crossFolder) &&
      // A label's view waits for its label (and its mailbox to be the active
      // one); the Inbox under it is not what was asked for.
      (!labelMode || (currentLabel !== undefined && labelAccountId === activeAccountId)) &&
      !(!labelMode && debouncedSearch !== '' && scopePending) &&
      (!searching || debouncedSearch !== '') &&
      flatListOn,
    staleTime: STALE_TIMES.list,
  });

  /** The conversation list (ADR 0077): disabled, and silent, unless it is the list. */
  const conversationList = useConversationList({
    enabled: conversationMode,
    unified,
    accountId: activeAccountId,
    folderId,
    inboxes: unifiedInboxes,
    sort: conversationSort ?? 'date-desc',
    unreadOnly: filter === 'unread',
    flaggedOnly: filter === 'flagged',
  });

  const flatQuery = unified ? unifiedQuery : folderQuery;
  /** What the view reads of whichever list is showing: its status, not its pages. */
  const listQuery: ListStatus = conversationMode ? conversationList.query : flatQuery;

  // Flatten the pages once. The dedupe guards the one case cursor paging can
  // repeat a row: a message arriving between two page fetches shifts the
  // window. It is keyed on account AND id, because a message id names no
  // account (ADR 0012) and the merged list holds several — deduping on the id
  // alone would silently drop one mailbox's row.
  const flatMessages = useMemo(() => {
    const seen = new Set<string>();
    const flat: MessageSummary[] = [];
    for (const page of flatQuery.data?.pages ?? []) {
      for (const message of page.items) {
        const key = `${message.accountId}\u0000${message.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        flat.push(message);
      }
    }
    return flat;
  }, [flatQuery.data]);
  /*
   * The rows on screen. In conversation mode each is a conversation dressed as
   * its newest message (`conversationRows.ts`), so everything below — cursor,
   * selection, menus, verbs — works on it unchanged, and the verbs expand it
   * to its members on the way to the server.
   */
  const messages: MessageSummary[] = conversationMode ? conversationList.rows : flatMessages;

  /**
   * A folder id's display name, in the row's OWN mailbox: folder ids are
   * `base64url(mailbox)` per account, so two mailboxes can mint the same id
   * for different folders and the lookup must be keyed by both.
   */
  const folderNameOf = useCallback(
    (accountId: string, id: string): string | undefined =>
      (accountFolders.get(accountId)?.folders ?? (accountId === activeAccountId ? folders : []))
        .find((candidate) => candidate.id === id)?.name,
    [accountFolders, activeAccountId, folders],
  );

  /** Rows carry their folder only while they come from more than one. */
  const folderLabelOf = useMemo(
    () =>
      crossFolder
        ? (message: MessageSummary) => folderNameOf(message.accountId, message.folderId)
        : undefined,
    [crossFolder, folderNameOf],
  );

  /**
   * Folders a cross-folder search could not read (timed out or failed), from
   * the newest page: the rows on screen are real, the list is just not all of
   * them, and the banner names which folders are missing.
   */
  const searchGaps = useMemo(() => {
    if (!crossFolder) return [];
    const pages = flatQuery.data?.pages ?? [];
    const last = pages[pages.length - 1] as (SearchPage & Partial<UnifiedPage>) | undefined;
    if (!last) return [];
    if (unified) return last.foldersMissed ?? [];
    const accountId = activeAccountId ?? '';
    return [
      ...(last.foldersTimedOut ?? []).map((folderId) => ({ accountId, folderId, reason: 'timeout' as const })),
      ...(last.foldersFailed ?? []).map((folderId) => ({ accountId, folderId, reason: 'failed' as const })),
    ];
  }, [activeAccountId, crossFolder, flatQuery.data, unified]);
  const searchPartial =
    crossFolder && (searchGaps.length > 0 || (!unified && flatQuery.data?.pages.some((page) => page.partial) === true));

  /**
   * The mailboxes the merged list could not read, for the banner (ADR 0056 §3).
   *
   * Two ways a mailbox goes missing, and both have to be said out loud:
   *
   * - its **message page** was refused — the lane ran and failed, and
   *   `fetchUnifiedPage` recorded it;
   * - its **folder list** was refused, so it never became a lane at all.
   *   This is the one that is easy to miss: with no Inbox id there is nothing
   *   to fetch, and without this the mailbox would simply not be in the list,
   *   which is indistinguishable from an empty mailbox.
   */
  const unifiedPages: readonly { unavailable: UnifiedPage['unavailable'] }[] | undefined = conversationMode
    ? (conversationList.query.data?.pages as UnifiedConversationPage[] | undefined)
    : unifiedQuery.data?.pages;
  const laneProblems = unified ? (unifiedPages?.[unifiedPages.length - 1]?.unavailable ?? []) : [];
  const unavailable = useMemo(() => {
    if (!unified) return [];
    const named = new Set(laneProblems.map((problem) => problem.accountId));
    const out = [...laneProblems];
    for (const accountId of laneAccountIds) {
      if (named.has(accountId)) continue;
      const tree = accountFolders.get(accountId);
      if (!tree || tree.inbox !== undefined) continue;
      // Still on its first attempt: "not yet" is not "not at all", and a
      // banner here would accuse a mailbox that is about to answer. From the
      // second failure on it is said out loud, without waiting for the retry
      // ladder to run out — see `AccountFolders.failureCount`.
      if (tree.isPending && tree.failureCount < 2) continue;
      out.push({ accountId, ...describeLaneFailure(tree.error) });
    }
    return out;
    // `laneProblems` is derived from the query data on every render; the data
    // itself is the dependency that actually moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountFolders, laneAccountIds, unified, unifiedPages]);

  // What this account's server said it can order by. Remembered so the NEXT
  // cold load knows before it asks — an order the server cannot do is a 501,
  // not a differently-ordered page.
  const serverSorts = flatQuery.data?.pages[0]?.sortsAvailable;
  // In the merged list the offer is the intersection of the lanes, minus the
  // orders that cannot be compared across servers.
  const viewSorts = unified
    ? unifiedSortsFrom(serverSorts ? [serverSorts] : [])
    : serverSorts;
  // A cross-folder search offers only the orders it can merge, and that
  // narrower menu is about the search, not the server: it must not be
  // remembered as what this account can sort by.
  const flatSortsAvailable = crossFolder
    ? (viewSorts ?? [...CROSS_FOLDER_SORTS]).filter((order) => CROSS_FOLDER_SORTS.includes(order))
    : viewSorts;
  useEffect(() => {
    if (crossFolder || conversationMode) return;
    rememberSortsAvailable(flatSortsAvailable);
  }, [conversationMode, crossFolder, rememberSortsAvailable, flatSortsAvailable]);
  // A conversation list offers its own two orders and nothing else (§2): the
  // rest are what turns it back into single messages, from the flat list.
  const sortsAvailable: MessageSortOrder[] | undefined = conversationMode
    ? [...CONVERSATION_SORTS]
    : flatSortsAvailable;
  const listSort: MessageSortOrder = conversationMode ? (conversationSort ?? sort) : sort;

  const hasMore = listQuery.hasNextPage;
  /**
   * The fetch was never dispatched, because the browser has no network.
   *
   * TanStack's default `networkMode: 'online'` parks a query offline instead
   * of running it: `status` stays `pending`, `fetchStatus` is `paused`, and
   * there is no error to read — so a view that asks `isError` learns nothing
   * and a view that asks `isPending` concludes "still loading", for ever. That
   * is not loading; it is a folder with no copy on this device and no way to
   * get one, which is the state ADR 0059 §4 exists to name.
   */
  const listPaused = listQuery.fetchStatus === 'paused';
  const listLoading =
    listQuery.isFetching ||
    (!listPaused && listQuery.isPending && (unified || folderId !== undefined)) ||
    // /mail before the folder list has said which folder is the Inbox: there
    // is no list yet, not an empty one. This showed "Nothing here" for half a
    // second, then the skeleton, then the rows — a false "empty" for a screen
    // reader and most of the phone's 0.25 layout shift.
    // (Before the account is known the folder query is not even running, so
    // "no folders and no error yet" counts as loading too.)
    (!unified && folderId === undefined && (foldersLoading || (folders.length === 0 && foldersError === undefined)));

  const remoteMode =
    remoteChoice !== undefined && remoteChoice.messageId === messageId
      ? remoteChoice.mode
      : undefined;

  const detailQuery = useQuery({
    // The remote mode is part of the key: a body with images blocked and the
    // same body with them loaded are two documents, not one refetched.
    // `paneAccountId`, not the active account: in the merged list the open
    // message belongs to whichever mailbox the row came from (ADR 0056 §5).
    queryKey: queryKeys.account(paneAccountId ?? '').message(messageId ?? '', remoteMode),
    queryFn: ({ signal }) =>
      api.messages.get(paneAccountId ?? '', messageId ?? '', {
        signal,
        ...(remoteMode ? { remote: remoteMode } : {}),
      }),
    enabled: paneAccountId !== undefined && messageId !== undefined,
    // A delivered body is immutable; its flags are patched, never refetched.
    // What the "always load" rules made of its images is not.
    staleTime: messageDetailStaleTime(remoteMode),
    // Load images: keep this message on screen, not the skeleton.
    placeholderData: (previous) =>
      sameMessagePlaceholder(previous, paneAccountId ?? '', messageId ?? ''),
  });
  // Opening a message here closes its notification, if one is still on
  // screen on this device (ADR 0106): the notice has done its job.
  useEffect(() => {
    if (paneAccountId !== undefined && messageId !== undefined) {
      void closeMessageNotices(paneAccountId, [messageId]);
    }
  }, [paneAccountId, messageId]);
  // Summarize / Explain / Ask on the open message (ADR 0074); inert while the assistant is off.
  // In conversation mode the conversation pane owns it (and Shift+S), and
  // summarizes the whole conversation: this one stands down.
  const aiSummary = useAiSummary(
    messageId !== undefined && !conversationMode ? detailQuery.data : undefined,
  );

  /**
   * Notification rules, for the context menu's Notify group only.
   *
   * This list exists so the menu can offer "Stop notifying" instead of a
   * duplicate "Always notify" that the unique index would refuse. It is **not**
   * an evaluator and is never consulted to decide whether to raise a
   * notification: that decision is the server's, always (ADR 0021 §4.4).
   */
  // The menu's row's mailbox: in the merged list that need not be the pane's.
  const menuAccountId = menu?.message.accountId || paneAccountId;
  const notifyRulesQuery = useQuery({
    queryKey: queryKeys.account(menuAccountId ?? '').notificationRules(),
    queryFn: ({ signal }) => api.notifications.listRules(menuAccountId ?? '', signal),
    enabled: menuAccountId !== undefined,
    staleTime: STALE_TIMES.metadata,
  });

  /**
   * Persisted remote-content rules, read only so the bar's "Stop always
   * loading" can resolve the applied rule to the row it has to delete.
   */
  const remoteRulesQuery = useQuery({
    queryKey: queryKeys.account(paneAccountId ?? '').remoteContentRules(),
    queryFn: ({ signal }) => api.remoteContent.listRules(paneAccountId ?? '', signal),
    enabled: paneAccountId !== undefined,
    staleTime: STALE_TIMES.metadata,
  });

  /**
   * The keyboard cursor is a message *id*, not an index (§5): ids survive a
   * page append, an optimistic removal and a re-sort, where an index silently
   * points at a different message. The open message always wins, so "focused
   * row" and "selected message" cannot disagree — which is what the old
   * sync-the-cursor effect was for. The one exception is a row asked to open
   * whose URL has not landed yet (`opening`, below).
   */
  const [cursorId, setCursorId] = useState<string | undefined>(undefined);
  /**
   * The open row, as a key: `messageId` alone names nothing in a merged list.
   * In conversation mode it is the conversation that holds the open message,
   * whichever member that is — the head, or an older one opened from the pane.
   */
  /*
   * The pane's conversation, shared with `ConversationPane` (one query, one
   * request): a conversation opened from a link, or not in the pages loaded,
   * is still a whole conversation to the toolbar and the keys.
   */
  const paneConversation = useConversation(
    conversationMode ? paneAccountId : undefined,
    conversationMode ? messageId : undefined,
  ).data;
  const openConversation = useMemo(() => {
    if (!conversationMode || messageId === undefined) return undefined;
    const listed = messages.find(
      (row) =>
        isConversationRow(row) &&
        row.accountId === (paneAccountId ?? '') &&
        conversationHolds(row, messageId),
    );
    if (listed) return listed;
    // The pane keeps the previous conversation on screen while the next one
    // loads: only one that holds this message stands for it.
    if (paneConversation && paneConversation.messages.some((member) => member.id === messageId)) {
      return toConversationRow(paneConversation.conversation);
    }
    return undefined;
  }, [conversationMode, messageId, messages, paneAccountId, paneConversation]);
  const openRowKey =
    messageId === undefined
      ? undefined
      : openConversation
        ? rowKey(openConversation)
        : rowKeyOf(paneAccountId, messageId);
  /*
   * A row asked to open whose URL has not landed yet (#330). The click moves
   * the cursor at once, but the router commits the navigation in a transition,
   * so for a moment `messageId` is still the message open BEFORE the click —
   * and "the open message always wins" put the cursor back on it. A key
   * pressed in that gap (`x` straight after a click) acted on the wrong row.
   * Tied to the location it was asked from: any navigation ends it, so it
   * cannot outlive the gap.
   */
  const [opening, setOpening] = useState<{ key: string; from: string } | undefined>(undefined);
  const openingKey = opening !== undefined && opening.from === location.key ? opening.key : undefined;
  /*
   * Closing the message leaves the cursor on the row that was open (#205).
   * j/k inside the pane walk the URL, not `cursorId`, so without this the
   * cursor fell back to the row first clicked and Back "returned" focus to
   * the wrong row. Adjusted during render, so the list never paints a frame
   * with the cursor elsewhere.
   */
  const [lastOpenKey, setLastOpenKey] = useState(openRowKey);
  if (openRowKey !== lastOpenKey) {
    if (openRowKey === undefined && lastOpenKey !== undefined) setCursorId(lastOpenKey);
    setLastOpenKey(openRowKey);
  }
  const activeCursorId = openingKey ?? openRowKey ?? cursorId;
  const cursorIndex = useMemo(() => {
    const index = messages.findIndex((message) => rowKey(message) === activeCursorId);
    return index >= 0 ? index : 0;
  }, [messages, activeCursorId]);

  const setCursorIndex = useCallback(
    (index: number) => {
      const target = messages[index];
      setCursorId(target ? rowKey(target) : undefined);
    },
    [messages],
  );

  /*
   * Every verb routes by `message.accountId` (ADR 0056 §4); the id below is
   * only the fallback for a batch with no rows in it. The role folders are
   * resolvers rather than ids, so Archive and Trash are the ones belonging to
   * the row's own mailbox.
   */
  const setFlags = useSetFlagsMutation(activeAccountId);
  const moveMessages = useMoveMessagesMutation(activeAccountId);
  const transferMessages = useTransferMessagesMutation();
  const copyIntoMailbox = useCopyMessages(activeAccountId);
  const archiveMessages = useArchiveMessagesMutation(activeAccountId, roleFolderOf('archive'));
  const deleteMessages = useDeleteMessagesMutation(activeAccountId, roleFolderOf('trash'));
  const reportSpamMessages = useReportSpamMutation(activeAccountId, roleFolderOf('junk'));
  const notSpamMessages = useNotSpamMutation(activeAccountId, inboxOf);
  const applyLabels = useApplyLabelsMutation();
  const mutationFailed =
    setFlags.isError ||
    archiveMessages.isError ||
    deleteMessages.isError ||
    moveMessages.isError ||
    reportSpamMessages.isError ||
    notSpamMessages.isError ||
    applyLabels.isError;

  /**
   * A bulk mutation that came back `200 {applied, failed}` with a non-empty
   * `failed` has already been rolled back in the hook; this is the sentence
   * that says so out loud (doc 11 §9.4).
   */
  const partialFailure =
    bulkFailureMessage(setFlags.data) ??
    bulkFailureMessage(archiveMessages.data) ??
    bulkFailureMessage(deleteMessages.data) ??
    bulkFailureMessage(reportSpamMessages.data) ??
    bulkFailureMessage(notSpamMessages.data) ??
    bulkFailureMessage(applyLabels.data);

  /**
   * ← and `u` (ADR 0048 §4): when the list opened this message, going back to
   * it *is* going back — `navigate(-1)`, exactly what the hardware or gesture
   * back does, so the two can never disagree and the history does not fill up
   * with list/message pairs. A message reached any other way (a link, a
   * reload, j/k from another message) has no list entry behind it, so the
   * folder *replaces* it: pushing would leave the message one back-press away.
   */
  const cameFromList = locationState.from === 'list';

  /**
   * The part of the URL that IS the result list: `?q=` and its scope. Every
   * navigation between the list and a message carries it, or opening a search
   * result would drop the search and put the folder listing back under the
   * reading pane. The scope is written out explicitly: a cross-folder result
   * opens in ITS folder, and a default decided by the route's folder would
   * then decide differently (Sent searches itself; the Inbox searches all).
   */
  const resultSearch = useMemo(() => {
    const kept = listSearchParams(searchParams);
    if (kept === '') return '';
    const params = new URLSearchParams(kept);
    params.set(SCOPE_PARAM, scope);
    return `?${params.toString()}`;
  }, [scope, searchParams]);

  const backToList = useCallback(() => {
    if (messageId !== undefined && cameFromList) {
      navigate(-1);
      return;
    }
    if (unified) {
      navigate(`${UNIFIED_PATH}${resultSearch}`, { replace: messageId !== undefined });
      return;
    }
    if (labelMode && labelAccountId !== undefined && labelIdParam !== undefined) {
      navigate(`${labelViewPath(labelAccountId, labelIdParam)}${resultSearch}`, {
        replace: messageId !== undefined,
      });
      return;
    }
    if (folderId) {
      navigate(`/mail/${encodeURIComponent(folderId)}${resultSearch}`, {
        replace: messageId !== undefined,
      });
    }
  }, [cameFromList, folderId, labelAccountId, labelIdParam, labelMode, messageId, navigate, resultSearch, unified]);

  /**
   * Opening a draft means editing it, not reading it.
   *
   * A draft IS a message in \Drafts, so the list pane shows it like any other
   * row; clicking one in any other mail client puts it back in the composer,
   * and doing anything else here would leave the Drafts folder as a place work
   * goes to be looked at and never finished. The full draft is fetched rather
   * than seeded from the row, because the row is an envelope: it has no body,
   * no Bcc and no attachments.
   */
  const openDraft = useCallback(
    (message: MessageSummary): void => {
      // The row's own mailbox: in the merged list a draft row can belong to
      // any of them.
      const account = message.accountId || activeAccountId;
      if (!account) return;
      api.compose.getDraft(account, message.id).then(
        (loaded) => {
          setCompose(seedFromDraft(loaded));
          setWindowNotice(loaded.warning);
        },
        (error: unknown) => {
          setWindowNotice(
            error instanceof ApiError && error.status === 404
              ? 'That draft is no longer there; it may have been sent or deleted elsewhere.'
              : 'That draft could not be opened.',
          );
        },
      );
    },
    // The setters are listed because the React Compiler infers them; without
    // them it declines to optimise this component at all.
    [activeAccountId, api, setCompose, setWindowNotice],
  );

  const draftsFolderId = folderByRole('drafts')?.id;

  /** Where a row opens: its own folder, or inside the view it was opened from. */
  const messagePathOf = useCallback(
    (target: MessageSummary): string =>
      unified
        ? unifiedMessagePath(target.accountId, target.id)
        : labelMode && labelAccountId !== undefined && labelIdParam !== undefined
          ? labelMessagePath(labelAccountId, labelIdParam, target.id)
          : `/mail/${encodeURIComponent(target.folderId)}/${encodeURIComponent(target.id)}`,
    [labelAccountId, labelIdParam, labelMode, unified],
  );

  const openMessage = useCallback(
    (message: MessageSummary, _index: number) => {
      const drafts = unified
        ? accountFolders.get(message.accountId)?.byRole('drafts')?.id
        : draftsFolderId;
      if (drafts !== undefined && message.folderId === drafts) {
        openDraft(message);
        return;
      }
      // The row's key, not its bare id: the cursor is compared by key, so a
      // bare id matched nothing and `u` left the cursor on the top row (#343).
      setCursorId(rowKey(message));
      setOpening({ key: rowKey(message), from: location.key });
      openFromListRef.current = true;
      // Only a message opened *from the list* is marked so: opening a second
      // row while one is already open (the desktop reading pane) pushes an
      // entry whose previous entry is a message, not the list.
      // The ROW's folder and account, never the route's: in a cross-folder
      // search the two differ, and the message id is only meaningful in its
      // own folder of its own mailbox.
      navigate(messagePathOf(message) + resultSearch, {
        state: messageId === undefined ? ({ from: 'list' } satisfies MailLocationState) : undefined,
      });
      // Marking read is no longer done here: the reading pane does it through
      // `useMarkReadAfter`, which honours Settings → "Mark as read after N
      // seconds". Marking on open would make that setting do nothing. The
      // default (on, 0 seconds) is exactly what this used to do.
    },
    [accountFolders, navigate, draftsFolderId, openDraft, messageId, messagePathOf, resultSearch, unified, location.key],
  );

  const moveCursor = useCallback(
    (delta: number) => {
      if (messages.length === 0) return;
      const next = Math.min(Math.max(cursorIndex + delta, 0), messages.length - 1);
      const target = messages[next];
      if (!target) return;
      // In stacked mode the reading pane is hidden, so j/k only move the cursor;
      // with the pane visible we follow the cursor like Gmail's auto-advance.
      if (messageId !== undefined) {
        navigate(messagePathOf(target) + resultSearch);
      } else {
        setCursorId(rowKey(target));
      }
    },
    [messages, cursorIndex, messageId, messagePathOf, navigate, resultSearch],
  );

  /**
   * The open message, as a row: the loaded one when it is in the list, and
   * otherwise its detail (a detail IS a summary). A message opened from a link,
   * a search or a page not yet loaded is not in `messages`, and the toolbar and
   * the keys used to act on row 0 instead (#343).
   */
  const detailData = detailQuery.data;
  const openMessageRow = useMemo(() => {
    if (openRowKey === undefined) return undefined;
    const row = messages.find((message) => rowKey(message) === openRowKey);
    if (row) return row;
    if (openConversation) return openConversation;
    if (!detailData || detailData.id !== messageId) return undefined;
    return { ...detailData, accountId: detailData.accountId || (paneAccountId ?? '') };
  }, [detailData, messageId, messages, openConversation, openRowKey, paneAccountId]);

  /** What the keys act on: the open message, else the cursor row. */
  const currentMessage = useMemo(
    () => (messageId !== undefined ? openMessageRow : messages[cursorIndex]),
    [messages, cursorIndex, messageId, openMessageRow],
  );
  const isOpen = useCallback(
    (target: MessageSummary) => openRowKey !== undefined && rowKey(target) === openRowKey,
    [openRowKey],
  );

  // F6 pane cycling, stacked-mode focus in and back, "Opened: …" (#205).
  const paneFocus = usePaneFocus({
    gridRef,
    listPaneRef,
    readingPaneRef,
    messageId,
    stacked: handheld || readingPane === 'off',
    openedSubject: messageId !== undefined ? openMessageRow?.subject : undefined,
    openFromListRef,
  });

  /**
   * "Add to Tasks" (ADR 0075) for these rows: each goes to its OWN mailbox
   * (the unified view mixes them). The open message's Message-ID is in its
   * headers; for any other row the server reads it from the mailbox.
   */
  const addToTasks = useCallback(
    (targets: readonly MessageSummary[]) => {
      if (targets.length === 0) return;
      setTaskTargets(
        targets.map((target) => {
          const accountId = target.accountId || (paneAccountId ?? activeAccountId ?? '');
          const open =
            detailData !== undefined && detailData.id === target.id && isOpen(target) ? detailData : undefined;
          return {
            accountId,
            messageId: target.id,
            subject: target.subject,
            from: target.from,
            date: target.date,
            rfcMessageId: open ? rfcMessageIdOf(open.headers) : undefined,
          };
        }),
      );
    },
    [activeAccountId, detailData, isOpen, paneAccountId, setTaskTargets],
  );

  /**
   * Snooze (ADR 0076) is offered for mail that can leave its folder and come
   * back: not a draft (that is the composer's) and not a copy waiting in
   * Scheduled. A row whose tree is not loaded yet is given the benefit of the
   * doubt, as Archive is; the server has the last word.
   */
  const canSnooze = useCallback(
    (targets: readonly MessageSummary[]) =>
      targets.length > 0 &&
      targets.every((target) => {
        const tree = accountFolders.get(target.accountId) ?? accountFolders.get(activeAccountId ?? '');
        if (!tree || tree.isPending) return true;
        return (
          tree.byRole('drafts')?.id !== target.folderId && tree.byRole('scheduled')?.id !== target.folderId
        );
      }),
    [accountFolders, activeAccountId],
  );

  /** Opens the Snooze chooser for these rows; each goes to its OWN mailbox. */
  const snooze = useCallback(
    (targets: readonly MessageSummary[]) => {
      if (!canSnooze(targets)) return;
      // A conversation row snoozes its whole conversation in this folder
      // (ADR 0077 §9), not just its newest message.
      setSnoozeTargets(expandConversations({ messages: [...targets] }).messages);
    },
    [canSnooze, setSnoozeTargets],
  );

  /**
   * "Label as" (plan/17 §5) for these rows: `l`, the toolbar, the row's menu,
   * the selection's Actions and the phone's ⋮ all come here. A label belongs
   * to ONE mailbox, so a batch spanning two is refused out loud rather than
   * offered one mailbox's labels for the other's mail.
   */
  const labelAs = useCallback(
    (targets: readonly MessageSummary[]) => {
      if (targets.length === 0) return;
      const mailboxes = new Set(targets.map((target) => target.accountId || (paneAccountId ?? activeAccountId ?? '')));
      if (mailboxes.size > 1) {
        reportActionNotice(
          'Labels belong to one mailbox. Select messages from a single mailbox to label them.',
        );
        return;
      }
      setLabelTargets([...targets]);
    },
    [activeAccountId, paneAccountId, setLabelTargets],
  );

  /** A chip's ×: that label off these messages, one request. */
  const removeLabel = useCallback(
    (accountId: string, messageIds: readonly string[], label: Label) =>
      applyLabels.apply({ accountId, messageIds: [...messageIds], add: [], remove: [label] }),
    [applyLabels],
  );

  /* --------------------------------------------------------- multi-select */

  /**
   * What is selected, pruned to what is loaded and still here: an archived or
   * live-removed row drops out by itself, and a bulk action can only ever be
   * sent for rows the user can see were selected. Empty is "not selecting".
   */
  const listIdentity = JSON.stringify(
    conversationMode ? conversationList.queryKey : unified ? unifiedKey : listKey,
  );
  const selectedMessages = useMemo(
    () =>
      selection.list === listIdentity && selection.ids.size > 0
        ? messages.filter((message) => selection.ids.has(rowKey(message)))
        : [],
    [listIdentity, messages, selection],
  );
  const selectedIds = useMemo(
    () => new Set(selectedMessages.map((message) => rowKey(message))),
    [selectedMessages],
  );
  const selecting = selectedMessages.length > 0;

  /*
   * The hardware back button (ADR 0048 §4). Entering selection mode on
   * handheld pushed an entry marked `selecting`; a POP to an entry without the
   * mark is back having been pressed, and it ends selection mode — the folder
   * stays. Adjusted during render, like the drawer's open state, so the list
   * never paints one frame still selected.
   */
  const [seenLocationKey, setSeenLocationKey] = useState(location.key);
  if (seenLocationKey !== location.key) {
    setSeenLocationKey(location.key);
    if (handheld && navigationType === 'POP' && !locationState.selecting && selection.ids.size > 0) {
      setSelection(NO_SELECTION);
    }
  }

  const exitSelection = useCallback(() => {
    setSelection(NO_SELECTION);
    setBulkMenu(undefined);
    // Take the entry selection mode pushed with it, so back afterwards leaves
    // the folder instead of visiting a dead copy of it.
    if (locationState.selecting) navigate(-1);
  }, [locationState.selecting, navigate, setBulkMenu, setSelection]);

  const writeSelection = useCallback(
    (ids: ReadonlySet<string>, anchorId: string | undefined) => {
      if (ids.size === 0) {
        exitSelection();
        return;
      }
      if (!selecting && handheld && !locationState.selecting) {
        navigate(
          { pathname: location.pathname, search: location.search },
          { state: { ...locationState, selecting: true } satisfies MailLocationState },
        );
      }
      setSelection({ list: listIdentity, ids, ...(anchorId !== undefined ? { anchorId } : {}) });
    },
    [
      exitSelection,
      handheld,
      listIdentity,
      location.pathname,
      location.search,
      locationState,
      navigate,
      selecting,
      setSelection,
    ],
  );

  const toggleSelected = useCallback(
    (message: MessageSummary) => {
      const key = rowKey(message);
      const next = new Set(selectedIds);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeSelection(next, key);
    },
    [selectedIds, writeSelection],
  );

  /** Shift-click: every row from the anchor to this one, added to the selection. */
  const selectRange = useCallback(
    (index: number) => {
      const anchor =
        selection.anchorId !== undefined
          ? messages.findIndex((message) => rowKey(message) === selection.anchorId)
          : -1;
      const from = anchor >= 0 ? anchor : cursorIndex;
      const next = new Set(selectedIds);
      for (let i = Math.min(from, index); i <= Math.max(from, index); i += 1) {
        const target = messages[i];
        if (target) next.add(rowKey(target));
      }
      const here = messages[index];
      writeSelection(next, selection.anchorId ?? (here ? rowKey(here) : undefined));
    },
    [cursorIndex, messages, selectedIds, selection.anchorId, writeSelection],
  );

  const selectAllLoaded = useCallback(() => {
    writeSelection(new Set(messages.map((message) => rowKey(message))), selection.anchorId);
  }, [messages, selection.anchorId, writeSelection]);

  /* ------------------------------------------- folder_invalidated (409) */

  /**
   * `409 folder_invalidated` means the folder's `UIDVALIDITY` changed, so every
   * cached id under it — rows, cursors and the open message — addresses
   * something that no longer exists. Refetching the *same* cursor would 409
   * forever and showing the rows we have would be showing stale ids as if they
   * were live, so the cache for this list is dropped and page one is fetched
   * again, exactly once per list identity. A second consecutive 409 falls
   * through to the error state rather than looping.
   */
  const recovery = useRef<{ key: string; attempts: number }>({ key: '', attempts: 0 });
  const listError = listQuery.error;
  const refetchList = listQuery.refetch;
  const detailError = detailQuery.error;

  const activeListKey = conversationMode ? conversationList.queryKey : unified ? unifiedKey : listKey;
  const reloadFolder = useCallback(() => {
    queryClient.removeQueries({ queryKey: activeListKey });
    void refetchList();
  }, [queryClient, activeListKey, refetchList]);

  useEffect(() => {
    const invalidated =
      (listError instanceof ApiError && listError.isFolderInvalidated) ||
      (detailError instanceof ApiError && detailError.isFolderInvalidated);
    if (!invalidated) return;

    const key = JSON.stringify(filters);
    const state = recovery.current;
    if (state.key !== key) {
      state.key = key;
      state.attempts = 0;
    }
    if (state.attempts >= 1) return;
    state.attempts += 1;

    queryClient.removeQueries({ queryKey: activeListKey });
    void refetchList();
    // The open message's id is stale too: keep the folder, drop the selection.
    if (detailError instanceof ApiError && detailError.isFolderInvalidated) backToList();
  }, [listError, detailError, filters, activeListKey, queryClient, refetchList, backToList]);

  /* -------------------------------------------------------- the actions */

  /**
   * Every action below is an optimistic mutation: the cache is patched now, the
   * request follows, and a failure restores the snapshot verbatim. Nothing here
   * invalidates the visible list — see src/cache/patches.ts.
   */
  /**
   * The conversation as a row, from its detail: the LISTED row when the list
   * holds it (its members are what the last sync said), else one rebuilt from
   * the detail. A verb acts on the row's `messageIds`, and a detail fetched
   * before a member was expunged elsewhere would name that member still —
   * which the server refuses, and one refused id rolls the whole verb back.
   */
  const conversationRowFor = useCallback(
    (conversation: ConversationSummary): MessageSummary =>
      messages.find(
        (row) =>
          isConversationRow(row) &&
          row.accountId === conversation.accountId &&
          row.conversation.id === conversation.id,
      ) ?? toConversationRow(conversation),
    [messages],
  );

  /**
   * What `e`, Delete and `!` act on: the row given, else the open one (or the
   * cursor row). In conversation mode the open one is its conversation — but
   * opened from a link, before the conversation has loaded, it is only the
   * one message, and acting on that would take one message out of a
   * conversation. So the verb waits for the conversation (the pane's own
   * query, joined, not sent twice) and acts on all of it. `deferred` says the
   * target is the open conversation, found after the fact.
   */
  const withTarget = useCallback(
    (target: MessageSummary | undefined, act: (victim: MessageSummary, deferred: boolean) => void) => {
      if (target !== undefined) {
        act(target, false);
        return;
      }
      if (
        !conversationMode ||
        messageId === undefined ||
        paneAccountId === undefined ||
        openConversation !== undefined
      ) {
        if (currentMessage) act(currentMessage, false);
        return;
      }
      const accountId = paneAccountId;
      const opened = messageId;
      queryClient
        .fetchQuery({
          queryKey: conversationKeys.detail(accountId, opened),
          queryFn: ({ signal }) => api.messages.conversation(accountId, opened, signal),
          staleTime: STALE_TIMES.list,
        })
        .then(
          (detail) => act(conversationRowFor(detail.conversation), true),
          () => reportActionNotice('That conversation could not be loaded, so nothing was changed.'),
        );
    },
    [
      api,
      conversationMode,
      conversationRowFor,
      currentMessage,
      messageId,
      openConversation,
      paneAccountId,
      queryClient,
    ],
  );

  const archive = useCallback(
    (target?: MessageSummary) =>
      withTarget(target, (victim, deferred) => {
        // The floor under the affordances, which are already withheld when this
        // is false: `e` and a swipe reach here without passing a button, and
        // doing nothing without a word was the bug (#343).
        if (!canArchive([victim])) {
          reportActionNotice('This mailbox has no Archive folder, so there is nowhere to archive to.');
          return;
        }
        archiveMessages.mutate(expandConversations({ messages: [victim] }));
        if (deferred || isOpen(victim)) backToList();
      }),
    [withTarget, canArchive, isOpen, backToList, archiveMessages],
  );

  const remove = useCallback(
    (target?: MessageSummary) =>
      withTarget(target, (victim, deferred) => {
        deleteMessages.mutate(expandConversations({ messages: [victim] }));
        if (deferred || isOpen(victim)) backToList();
      }),
    [withTarget, isOpen, backToList, deleteMessages],
  );

  /**
   * `!` (Gmail's spam key), the toolbar button and the menu item: Report spam,
   * or Not spam for a row already in Junk. Either way the row leaves the list
   * it is in, so an open message closes, as Archive does.
   */
  const spam = useCallback(
    (target?: MessageSummary) =>
      withTarget(target, (victim, deferred) => {
        const verb = spamVerbFor([victim]);
        if (verb === undefined) return;
        if (verb === 'not-spam') notSpamMessages.mutate(expandConversations({ messages: [victim] }));
        else reportSpamMessages.mutate(expandConversations({ messages: [victim] }));
        if (deferred || isOpen(victim)) backToList();
      }),
    [backToList, isOpen, notSpamMessages, reportSpamMessages, spamVerbFor, withTarget],
  );

  /**
   * Whether "Block sender…" can work for a row: a block is a Sieve rule on the
   * row's OWN mailbox's host, primary or connected alike, so it is withheld
   * only where that host positively has no managed filtering
   * (`capabilities.sieve === false`, which would answer `501
   * unsupported_by_host`). An account list that has not answered yet is not
   * that knowledge.
   */
  const canBlockSender = useCallback(
    (target: { accountId: string }) => {
      const owner = accounts.find(
        (account) => account.id === (target.accountId || paneAccountId),
      );
      return owner?.capabilities.sieve !== false;
    },
    [accounts, paneAccountId],
  );

  /**
   * "Block sender…" on a row or the open message: opens the confirmation.
   *
   * The block goes to the row's own mailbox. A detail that came without an
   * account falls back to the pane's — the open message's mailbox in the
   * merged list — never to the active one, which there is whichever mailbox
   * the sidebar last pointed at.
   */
  const askToBlock = useCallback(
    (target: { accountId: string; folderId: string; from: { email: string } }) => {
      const address = target.from.email.trim();
      const account = target.accountId || paneAccountId;
      if (address === '' || !account) return;
      setBlockTarget({ accountId: account, address, folderId: target.folderId });
    },
    [paneAccountId, setBlockTarget],
  );

  const toggleFlag = useCallback(
    (target: MessageSummary) => {
      // A conversation is starred when any member is: the star on its row
      // unstars every member, and stars every member otherwise.
      const flagged = target.flags.includes('flagged');
      setFlags.mutate(
        expandConversations({
          messages: [target],
          update: flagged ? { remove: ['flagged'] } : { add: ['flagged'] },
        }),
      );
    },
    [setFlags],
  );

  const setRead = useCallback(
    (target: MessageSummary, read: boolean) => {
      // Unread on the open message outranks the pane's mark-read countdown,
      // and must not be undone by it (#343).
      if (!read && isOpen(target)) {
        keepUnread(target.id);
        // The open message of a conversation need not be its head.
        if (messageId !== undefined) keepUnread(messageId);
      }
      setFlags.mutate(
        expandConversations({
          messages: [target],
          update: read ? { add: ['seen'] } : { remove: ['seen'] },
        }),
      );
    },
    [isOpen, messageId, setFlags],
  );

  const moveTo = useCallback(
    (target: MessageSummary, targetFolderId: string) => {
      moveMessages.mutate(expandConversations({ messages: [target], targetFolderId }));
      if (isOpen(target)) backToList();
    },
    [backToList, isOpen, moveMessages],
  );

  /**
   * The bulk verbs. Each is ONE call to the same mutation hook the single verb
   * uses, with every selected message in it — so the batch is one optimistic
   * patch, one deferred commit and one "3 messages archived · Undo" that puts
   * all three back (plan 15 §6.1). Completing any of them ends selection mode
   * (ADR 0019 §2.2: "after a bulk item runs, the selection clears").
   */
  const finishBulk = useCallback(
    (removes: boolean) => {
      exitSelection();
      if (removes && openRowKey !== undefined && selectedIds.has(openRowKey)) backToList();
    },
    [backToList, exitSelection, openRowKey, selectedIds],
  );
  const bulk = useMemo(
    () => ({
      archive: () => {
        if (!canArchive(selectedMessages)) return;
        archiveMessages.mutate(expandConversations({ messages: selectedMessages }));
        finishBulk(true);
      },
      remove: () => {
        deleteMessages.mutate(expandConversations({ messages: selectedMessages }));
        finishBulk(true);
      },
      /*
       * One request per account and one Undo for the batch, like the rest.
       * Report spam leaves out rows that are already in Junk: there is nothing
       * to move them to, and they are not what the verb is about.
       */
      spam: () => {
        const verb = spamVerbFor(selectedMessages);
        if (verb === undefined) return;
        if (verb === 'not-spam') {
          notSpamMessages.mutate(expandConversations({ messages: selectedMessages }));
        } else {
          const outside = selectedMessages.filter((message) => junkOf(message) !== message.folderId);
          reportSpamMessages.mutate(expandConversations({ messages: outside }));
        }
        finishBulk(true);
      },
      move: (targetFolderId: string) => {
        moveMessages.mutate(expandConversations({ messages: selectedMessages, targetFolderId }));
        finishBulk(true);
      },
      setRead: (read: boolean) => {
        if (!read) {
          for (const message of selectedMessages) {
            if (!isOpen(message)) continue;
            keepUnread(message.id);
            if (messageId !== undefined) keepUnread(messageId);
          }
        }
        setFlags.mutate(
          expandConversations({
            messages: selectedMessages,
            update: read ? { add: ['seen'] } : { remove: ['seen'] },
          }),
        );
        finishBulk(false);
      },
      setStarred: (starred: boolean) => {
        setFlags.mutate(
          expandConversations({
            messages: selectedMessages,
            update: starred ? { add: ['flagged'] } : { remove: ['flagged'] },
          }),
        );
        finishBulk(false);
      },
    }),
    [
      archiveMessages,
      canArchive,
      deleteMessages,
      finishBulk,
      isOpen,
      junkOf,
      messageId,
      moveMessages,
      notSpamMessages,
      reportSpamMessages,
      selectedMessages,
      setFlags,
      spamVerbFor,
    ],
  );

  /**
   * Into a folder of ANY mailbox (ADR 0108). Rows already in the target's
   * mailbox take the ordinary verb (one undoable move, or a copy); the rest
   * are transferred between mailboxes, per source account, with no Undo.
   *
   * The target's folders are read from the cache, for its name in the notice:
   * a drop target or a menu item is only on screen once its tree has loaded.
   */
  const foldersOfAccount = useCallback(
    (accountId: string) => queryClient.getQueryData<Folder[]>(queryKeys.account(accountId).folders()) ?? [],
    [queryClient],
  );
  const moveOrTransfer = useCallback(
    (rows: readonly MessageSummary[], targetAccountId: string, targetFolderId: string, mode: DropMode) => {
      const same = rows.filter((row) => (row.accountId || activeAccountId) === targetAccountId);
      const others = rows.filter((row) => (row.accountId || activeAccountId) !== targetAccountId);
      if (same.length > 0) {
        if (mode === 'move') {
          moveMessages.mutate(expandConversations({ messages: same, targetFolderId }));
        } else {
          const name = foldersOfAccount(targetAccountId).find((f) => f.id === targetFolderId)?.name ?? 'the folder';
          const { messages: members } = expandConversations({ messages: same });
          copyIntoMailbox.mutate(
            { messages: members, targetFolderId, accountId: targetAccountId },
            {
              onSuccess: (result) =>
                reportActionNotice(
                  result.failed.length > 0
                    ? 'Some messages could not be copied.'
                    : `${members.length === 1 ? 'Message' : `${members.length} messages`} copied to ${name}.`,
                ),
              onError: () => reportActionNotice('Those messages could not be copied.'),
            },
          );
        }
      }
      if (others.length > 0) {
        const account = accounts.find((candidate) => candidate.id === targetAccountId);
        const folder = foldersOfAccount(targetAccountId).find((f) => f.id === targetFolderId);
        const targetLabel = `${folder?.name ?? 'the folder'} in ${account?.email ?? 'the other mailbox'}`;
        const { messages: members } = expandConversations({ messages: [...others] });
        transferMessages.mutate({ messages: members, targetAccountId, targetFolderId, targetLabel, mode });
      }
    },
    [accounts, activeAccountId, copyIntoMailbox, foldersOfAccount, moveMessages, transferMessages],
  );

  /**
   * Dragging rows onto the folder tree (#168). What a drag carries is the
   * selection when the grabbed row is in it (Gmail), else that one row; the
   * drop is the same single move mutation the `v` palette makes, so it is one
   * optimistic patch and one "N messages moved · Undo". Rows already in the
   * target (a cross-folder search) are left where they are.
   */
  const dragRows = useCallback(
    (message: MessageSummary) =>
      selectedIds.has(rowKey(message))
        ? { rows: selectedMessages, fromSelection: true }
        : { rows: [message], fromSelection: false },
    [selectedIds, selectedMessages],
  );
  useEffect(
    () =>
      registerDropHandler((drag, target, mode) => {
        const rows = rowsToMove(drag, target);
        moveOrTransfer(rows, target.accountId, target.id, mode);
        const removes = mode === 'move';
        if (drag.fromSelection) finishBulk(removes);
        else if (removes && rows.some((row) => isOpen(row))) backToList();
      }),
    [backToList, finishBulk, isOpen, moveOrTransfer],
  );

  /**
   * Double-click and `Shift+Enter` (ADR 0020).
   *
   * A blocked popup is not allowed to be a no-op: the message opens in the pane
   * instead and the live region says why.
   */
  const openInWindow = useCallback(
    (target: MessageSummary, index: number) => {
      const result = openMessageWindow({
        accountId: target.accountId || (activeAccountId ?? ''),
        messageId: target.id,
      });
      if (result.opened) {
        setWindowNotice(undefined);
        return;
      }
      setWindowNotice('Your browser blocked the window; opened here instead.');
      openMessage(target, index);
    },
    [activeAccountId, openMessage, setWindowNotice],
  );

  /*
   * The address a reply is from: the open message's mailbox in the merged
   * list, the active one otherwise. Reply-all drops it from the recipients and
   * the composer sends from it (#256), so getting this wrong in the unified
   * view would answer a colleague from the wrong mailbox.
   */
  const paneAccount = accounts.find((account) => account.id === paneAccountId) ?? activeAccount;
  const ownAddress = paneAccount?.email;

  /**
   * The account the open composer belongs to: the one active when it opened.
   *
   * Switching accounts mid-message must not re-address it. Its draft id, its
   * reply parent and its From all belong to the mailbox it was started in, and
   * the server now sends as whichever account the URL names -- so following the
   * switcher would send the half-written message from a different mailbox.
   * Set during render rather than in an effect, so the composer never renders
   * once against the wrong account.
   */
  const [composeAccount, setComposeAccount] = useState<{ id: string; email: string }>();
  /** Why a returned composer is back: a queued send that failed on commit. */
  const [composeProblem, setComposeProblem] = useState<string | undefined>(undefined);

  /*
   * A composer opened in place -- Reply, Forward, a draft -- has no history
   * entry of its own (the FAB's `?compose=` does), so a phone's Back used to
   * leave the MESSAGE with the composer still over the list. Back now closes
   * the composer first; closing unmounts it, and the draft hook saves the
   * unsaved text on the way out, as it does for the ✕.
   */
  useBackToClose(compose !== undefined && composeIntent === null, closeCompose);

  const composeOpen = composeSeed !== undefined;
  /**
   * Pins the next composer to a row's own mailbox (ADR 0056 §4). In the merged
   * list a reply or forward from the menu is about a row that may belong to a
   * different mailbox than the open message, and answering it from the pane's
   * would send from the wrong address (#343).
   */
  const pinComposeTo = useCallback(
    (accountId: string | undefined) => {
      const account = accounts.find((candidate) => candidate.id === accountId);
      if (account) setComposeAccount({ id: account.id, email: account.email });
    },
    [accounts, setComposeAccount],
  );
  if (composeOpen && composeAccount === undefined && paneAccountId && ownAddress) {
    setComposeAccount({ id: paneAccountId, email: ownAddress });
  } else if (!composeOpen && composeAccount !== undefined) {
    setComposeAccount(undefined);
  }

  /**
   * A scheduled send taken back with Undo (ADR 0076): the server moved the
   * copy to Drafts and named it, and it reopens here pinned to its own
   * mailbox, exactly as a draft opened from Drafts would.
   */
  const reopenScheduledDraft = useCallback(
    (accountId: string, draftId: string) => {
      api.compose.getDraft(accountId, draftId).then(
        (loaded) => {
          const account = accounts.find((candidate) => candidate.id === accountId);
          if (account) setComposeAccount({ id: account.id, email: account.email });
          setCompose(seedFromDraft(loaded));
        },
        () => setWindowNotice('The message is back in Drafts, but it could not be opened here.'),
      );
    },
    [accounts, api, setCompose, setComposeAccount, setWindowNotice],
  );

  /**
   * A message coming back from the send undo window (issue #182).
   *
   * Undo -- and a committed send that then failed -- hand the whole composer
   * back through `features/send-undo`, which holds it outside React because
   * the window outlives this view. Picked up on mount as well as on the
   * announcement: Undo pressed from Calendar stashes the draft and navigates
   * here, so there was no listener at the moment it was handed over.
   *
   * The account is restored from the hand-over rather than from the switcher,
   * so a message started in one mailbox comes back pinned to that mailbox
   * however many times the user has switched since (#256).
   */
  useEffect(() => {
    const apply = () => {
      const returned = takeRestoredSend();
      if (returned === undefined) return;
      setCompose(returned.seed);
      setComposeAccount({ id: returned.accountId, email: returned.fromAddress });
      setComposeProblem(returned.problem);
    };
    apply();
    return subscribeRestoredSend(apply);
  }, []);

  /**
   * Forward, from any of its three ways in (button, `f`, the row's menu).
   *
   * Unlike a reply this is not seeded locally. The server builds it: it
   * re-stages the original's attachments so the composer opens with them as
   * ready chips, and it quotes the body through the OUTGOING sanitizer rather
   * than the reading pane's display copy. The price is a round trip, so the
   * button says "Forwarding…" meanwhile and a second press is ignored; the
   * gain is that a row the user never opened forwards exactly as well.
   *
   * A refusal (the files are over the attachment ceiling) opens no composer:
   * one without the files would be the silent drop the server refused to
   * make. It is said on the message instead.
   */
  const [forwarding, setForwarding] = useState<string | undefined>(undefined);
  const [forwardProblem, setForwardProblem] = useState<
    { messageId: string; text: string } | undefined
  >(undefined);
  const forwardInFlight = useRef(false);
  const openForward = useCallback(
    (target: Pick<MessageSummary, 'id' | 'accountId'> | undefined) => {
      const targetId = target?.id;
      // The row's own mailbox: a message id names nothing in another (#343).
      const account = target?.accountId || paneAccountId;
      if (!account || !targetId || forwardInFlight.current) return;
      forwardInFlight.current = true;
      setForwarding(targetId);
      setForwardProblem(undefined);
      api.compose.prepareReply(account, targetId, 'forward').then(
        (prefill) => {
          pinComposeTo(account);
          setCompose(forwardSeed(prefill));
        },
        (error: unknown) => {
          setForwardProblem({ messageId: targetId, text: forwardFailureCopy(error) });
        },
      ).finally(() => {
        forwardInFlight.current = false;
        setForwarding(undefined);
      });
    },
    [paneAccountId, api, pinComposeTo, setCompose, setForwardProblem, setForwarding],
  );

  /**
   * Reply from the menu, where the target is not necessarily the open message.
   *
   * With the detail loaded the seed quotes the body; without it (a row the user
   * never opened) it seeds the addresses and subject only, which is honest —
   * the body is not in the cache and a reply must not wait on a fetch.
   *
   * From the ROW's mailbox: in the merged list the pane's may be another one
   * (#343), and reply-all drops the address it is sent from.
   */
  const replyToMessage = useCallback(
    (target: MessageSummary, mode: 'reply' | 'reply-all') => {
      const account = accounts.find((candidate) => candidate.id === target.accountId) ?? paneAccount;
      const own = account?.email ?? ownAddress;
      const aliases = account?.aliases ?? [];
      const detail = detailQuery.data;
      pinComposeTo(target.accountId);
      setCompose(
        detail && isOpen(target)
          ? replySeed(detail, mode, own, aliases)
          : summaryReplySeed(target, mode, own, aliases),
      );
    },
    [accounts, detailQuery.data, isOpen, ownAddress, paneAccount, pinComposeTo, setCompose],
  );

  const openReply = useCallback(
    (mode: 'reply' | 'reply-all' | 'forward') => {
      if (mode === 'forward') {
        // The open message; with none open, the cursor row. The server needs
        // only the id, so there is no reason for `f` to do nothing in the list.
        openForward(currentMessage);
        return;
      }
      const detail = detailQuery.data;
      if (messageId !== undefined && detail) {
        setCompose(replySeed(detail, mode, ownAddress, paneAccount?.aliases));
        return;
      }
      // Nothing open: `r` and `a` answer the cursor row, as `f` forwards it,
      // rather than opening a blank composer (#343).
      if (currentMessage) replyToMessage(currentMessage, mode);
    },
    [currentMessage, detailQuery.data, messageId, openForward, ownAddress, paneAccount, replyToMessage, setCompose],
  );

  /**
   * The Notify group's two verbs, one request each.
   *
   * Success invalidates the rules list rather than patching it: this is a
   * settings-shaped list read once per view, not a virtualized list under
   * someone's cursor, so a refetch costs nothing anyone can feel.
   */
  // Keyed by the row's mailbox, which the menu passes in (#343): a rule is
  // per account, and the merged list's rows are not all the pane's.
  const notifyRuleMutation = useMutation({
    mutationFn: (input: { accountId: string; address: string; action: 'notify' | 'mute' }) =>
      api.notifications.addRule(input.accountId, {
        kind: 'sender',
        value: input.address,
        action: input.action,
      }),
    onSettled: (_data, _error, input) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.account(input.accountId).notificationRules(),
      }),
  });

  const clearNotifyRuleMutation = useMutation({
    mutationFn: (input: { accountId: string; ruleId: string }) =>
      api.notifications.removeRule(input.accountId, input.ruleId),
    onSettled: (_data, _error, input) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.account(input.accountId).notificationRules(),
      }),
  });

  /** The rule covering one address, if there is one. */
  const notifyRuleFor = useCallback(
    (address: string) =>
      (notifyRulesQuery.data ?? []).find(
        (rule) => rule.kind === 'sender' && rule.value === address.trim().toLowerCase(),
      ),
    [notifyRulesQuery.data],
  );

  /**
   * A row's own mailbox's folders (ADR 0056 §4). The menu's Move to, Copy to
   * and Archive are built from these: in the merged list the active account's
   * folder ids name nothing in another mailbox (#343).
   */
  const foldersOf = useCallback(
    (accountId: string | undefined) =>
      (accountId !== undefined ? accountFolders.get(accountId)?.folders : undefined) ?? folders,
    [accountFolders, folders],
  );

  /** Copy to… and Download original (.eml), for the menu's row's mailbox. */
  const copyMessages = useCopyMessages(menuAccountId);
  const messageDownload = useMessageDownload(menuAccountId);
  const copyTo = useCallback(
    (target: MessageSummary, targetFolderId: string) => {
      const name = foldersOf(target.accountId).find((f) => f.id === targetFolderId)?.name ?? 'the folder';
      copyMessages.mutate(
        { messages: [target], targetFolderId },
        {
          onSuccess: (result) =>
            reportActionNotice(
              result.failed.length > 0 ? 'That message could not be copied.' : `Message copied to ${name}.`,
            ),
          onError: () => reportActionNotice('That message could not be copied.'),
        },
      );
    },
    [copyMessages, foldersOf],
  );
  const downloadError = messageDownload.error;
  useEffect(() => {
    if (downloadError !== undefined) reportActionNotice(downloadError);
  }, [downloadError]);

  /**
   * "Move to…" from the reading pane's ⋮ (#343): the message menu's own
   * Move-to palette, opened detached on the open message, anchored under the
   * pane's toolbar on desktop and as a sheet on handheld.
   */
  const openMoveToFor = useCallback(
    (target: MessageSummary) => {
      const rect = document
        .querySelector('.pane--reading .reading-actions, .pane--reading .reading-appbar')
        ?.getBoundingClientRect();
      // Back to the button (or menu row) that asked for it, when the menu
      // closes; the list only when nothing in the pane had focus (#205).
      const opener = document.activeElement;
      const fromPane = opener instanceof HTMLElement && opener.closest('.pane--reading') !== null;
      setMenu({
        message: target,
        anchor: rect ? { x: rect.right, y: rect.bottom } : { x: 0, y: 0 },
        openMoveTo: true,
        returnFocusTo: fromPane ? opener : gridRef.current,
      });
    },
    [setMenu],
  );

  /** Opens the menu on the cursor row, for the delegated triggers and for `V`. */
  const openMenu = useCallback(
    (request: MessageContextMenuRequest, openMoveTo = false) => {
      const target = messages[request.index];
      if (!target) return;
      // ADR 0019 §2.2: inside the selection the menu is about the selection;
      // outside it, about that row, and the selection is left alone.
      if (selectedIds.has(rowKey(target))) {
        setBulkMenu({ anchor: request.anchor, returnFocusTo: gridRef.current });
        return;
      }
      setMenu({
        message: target,
        anchor: request.anchor,
        openMoveTo,
        // Captured at open time, not read during render: this is the element
        // focus goes back to, and it must be the grid container.
        returnFocusTo: gridRef.current,
      });
    },
    [messages, selectedIds, setBulkMenu, setMenu],
  );

  /*
   * mod+1…9: the Nth mailbox, in the switcher's order (#193, plan 06 §6), the
   * way the switcher picks it -- out of "All inboxes", and to the password
   * modal for one whose password was rejected. Bound only for mailboxes that
   * exist, and only with a choice of them, so a Ctrl/⌘+digit that means
   * nothing here stays the browser's (its tab switching). Never inside a text
   * field: `useKeyboardShortcuts` stands down there for these.
   */
  const pickMailbox = usePickMailbox();
  const mailboxKeys = useMemo(
    () =>
      accounts.length < 2
        ? {}
        : Object.fromEntries(
            accounts.slice(0, 9).map((account, index) => [`mod+${index + 1}`, () => pickMailbox(account.id)]),
          ),
    [accounts, pickMailbox],
  );

  /**
   * THE MAIL KEYMAP — Gmail conventions. Add a binding here and document it in
   * GLOBAL_SHORTCUT_DOCS; nothing else needs to change.
   */
  useKeyboardShortcuts(
    {
      ...mailboxKeys,
      j: () => moveCursor(1),
      k: () => moveCursor(-1),
      Enter: () => {
        const target = messages[cursorIndex];
        if (target) openMessage(target, cursorIndex);
      },
      // Mail-view scope, not only the focused list (#343): with the pane or
      // the page focused, the cursor row still opens in a window.
      'shift+Enter': () => {
        const target = currentMessage;
        if (target) openInWindow(target, messages.indexOf(target));
      },
      u: () => backToList(),
      // Pane cycling (#205, doc 06): F6 where the browser lets it through,
      // Ctrl/⌘+Alt+arrows as the same thing for keyboards without it.
      F6: () => paneFocus.cyclePane(1),
      'shift+F6': () => paneFocus.cyclePane(-1),
      'mod+alt+ArrowRight': () => paneFocus.cyclePane(1),
      'mod+alt+ArrowLeft': () => paneFocus.cyclePane(-1),
      // With a selection, the action keys act on all of it (Gmail).
      e: () => (selecting ? bulk.archive() : archive()),
      Delete: () => (selecting ? bulk.remove() : remove()),
      // A Mac keyboard's "delete" key reports Backspace. Never inside a text
      // field: `useKeyboardShortcuts` stands down there.
      Backspace: () => (selecting ? bulk.remove() : remove()),
      // Gmail's spam key. A shifted character, so the binding is the
      // character itself (see `normalise`), exactly like `?`.
      '!': () => (selecting ? bulk.spam() : spam()),
      z: () => undoPending(),
      // Gmail's "Add to Tasks". With a selection, one task per message.
      'shift+t': () => {
        if (selecting) addToTasks(selectedMessages);
        else if (currentMessage) addToTasks([currentMessage]);
      },
      // Gmail's Snooze (ADR 0076). With a selection, all of it.
      b: () => {
        if (selecting) snooze(selectedMessages);
        else if (currentMessage) snooze([currentMessage]);
      },
      // The cursor row, not the open message: they differ for the moment
      // between a click and its URL landing (#330), and `x` is the key pressed
      // straight after a click. A message open but not in the list has no
      // row, and is still the one toggled (#343).
      x: () => {
        const target = messages.find((message) => rowKey(message) === activeCursorId) ?? currentMessage;
        if (target) toggleSelected(target);
      },
      // Every row the list shows — the filter's, so with Unread on it is all
      // the unread mail loaded (the Select all button's set). Not inside a
      // text field: there Ctrl+A is the field's own select-all.
      'mod+a': () => {
        if (messages.length > 0) selectAllLoaded();
      },
      s: () => {
        if (selecting) bulk.setStarred(selectedMessages.some((m) => !m.flags.includes('flagged')));
        else if (currentMessage) toggleFlag(currentMessage);
      },
      c: () => setCompose({ mode: 'new' }),
      r: () => openReply('reply'),
      a: () => openReply('reply-all'),
      f: () => openReply('forward'),
      '/': () => {
        // The top bar owns the search box; focus it by id so the binding lives
        // in one keymap rather than being split across components.
        document.getElementById('global-search')?.focus();
      },
      '?': () => setShortcutsOpen(true),
      'shift+u': () => {
        if (selecting) bulk.setRead(false);
        else if (currentMessage) setRead(currentMessage, false);
      },
      // New in ADR 0019: the menu shows a shortcut for every item, so "Mark as
      // read" needed one to show.
      'shift+i': () => {
        if (selecting) bulk.setRead(true);
        else if (currentMessage) setRead(currentMessage, true);
      },
      // "Label as" (plan/17): the selection, else the open message or cursor row.
      l: () => {
        if (selecting) labelAs(selectedMessages);
        else if (currentMessage) labelAs([currentMessage]);
      },
      // The Move-to palette, opened detached on the cursor row.
      v: () => {
        // The open message, or the cursor row (#343).
        const target = currentMessage;
        if (!target) return;
        const rect = gridRef.current
          ?.querySelector(`[data-index="${messages.indexOf(target)}"]`)
          ?.getBoundingClientRect();
        // With a selection, Move to is the selection's.
        if (selecting) {
          setBulkMenu({ anchor: rect ?? { x: 0, y: 0 }, returnFocusTo: gridRef.current, openMoveTo: true });
          return;
        }
        setMenu({
          message: target,
          anchor: rect ?? { x: 0, y: 0 },
          openMoveTo: true,
          returnFocusTo: gridRef.current,
        });
      },
      // Going somewhere starts from that place's whole list: `g s`'s Starred
      // filter used to follow `g i` into the Inbox (#343).
      'g i': () => {
        setFilter('all');
        if (inbox) navigate(`/mail/${encodeURIComponent(inbox.id)}`);
      },
      'g s': () => setFilter('flagged'),
      'g c': () => navigate('/calendar'),
      Escape: () => {
        const consumed = shortcutsOpen || selecting;
        setShortcutsOpen(false);
        if (selecting && composeSeed === undefined) exitSelection();
        closeCompose();
        // Otherwise Escape is `u`: back to the list. With the reading pane
        // off that is the only way back short of the button, and in the
        // split layouts it closes the message, as it does in Gmail.
        if (!consumed && messageId !== undefined) backToList();
      },
    },
    /**
     * The keyboard-scope stack of doc 06 §11, in its smallest honest form: the
     * global keymap is off while a modal *or the context menu* owns the
     * keyboard, so `e` inside the menu is type-ahead and not Archive.
     */
    {
      // The key that closes a modal is the modal's, never also this page's
      // (useKeyboardShortcuts explains the race `enabled` alone loses).
      ignoreInsideModals: true,
      // The shortcut sheet is a modal too: `e` behind it archived (#343).
      enabled:
        composeSeed === undefined &&
        menu === undefined &&
        bulkMenu === undefined &&
        blockTarget === undefined &&
        taskTargets === undefined &&
        snoozeTargets === undefined &&
        labelTargets === undefined &&
        !shortcutsOpen,
    },
  );

  /**
   * Mail that has arrived and is deliberately NOT in the list yet.
   *
   * Splicing a row into a virtualized list moves every row under the cursor and
   * under the pointer of someone mid-triage, so an arrival waits behind this
   * pill until the user asks for it (doc 06 §5). This is the rule for *both*
   * sources of arrivals — the SSE `new_mail` frame and the interim poll — which
   * is why they write to one store and this reads one count.
   */
  /*
   * The merged list's pill is the sum over its lanes' Inboxes (ADR 0056): the
   * arrivals store is already keyed (account, folder), so this is an addition
   * rather than a second store, and the rule it preserves is the one that
   * matters — nothing splices itself into the list until the pill is pressed,
   * which is also what keeps the merge's ordering guarantee true under live
   * mail.
   */
  const folderArrivals = useArrivalCount(activeAccountId, folderId);
  const unifiedArrivals = useUnifiedArrivalCount(unifiedInboxes);
  // A conversation list takes new mail in by itself, in place (its `sync`,
  // ADR 0077), so there is nothing to hold behind a pill.
  // A label's view is not the Inbox the route falls back to: no pill for it.
  const newArrivals = conversationMode || labelMode ? 0 : unified ? unifiedArrivals : folderArrivals;
  const streamStatus = useAccountStreamStatus(activeAccountId);

  /** Clears whichever lanes this list is showing. */
  const clearListArrivals = useCallback(() => {
    if (unified) {
      for (const lane of unifiedInboxes) clearArrivals(lane.accountId, lane.folderId);
      return;
    }
    if (activeAccountId === undefined || folderId === undefined) return;
    clearArrivals(activeAccountId, folderId);
  }, [activeAccountId, folderId, unified, unifiedInboxes]);

  /**
   * The live stream was interrupted and cannot say what it missed.
   *
   * Different from an arrival in one way that decides everything about how it
   * is handled: it names no messages, so there is nothing to park and nothing
   * to count. The only way to make it good is a refetch of this view.
   */
  const streamGap = useStreamGap();

  const showArrivals = useCallback(() => {
    clearListArrivals();
    clearStreamGap();
    void listQuery.refetch();
  }, [clearListArrivals, listQuery]);

  /*
   * Asking for a mailbox whose password was rejected opens the password modal
   * (#192, plan 06 §6 step 3) instead of a refetch the server will refuse.
   */
  const promptReauth = useReauthPrompt();
  const needsOwnPassword = (accountId: string) => {
    const account = accounts.find((candidate) => candidate.id === accountId);
    return account !== undefined && !account.isPrimary && account.status === 'reauth-required';
  };
  const activeNeedsPassword =
    !unified && activeAccount !== undefined && !activeAccount.isPrimary && activeAccount.status === 'reauth-required';
  const refresh = useCallback(() => {
    if (activeNeedsPassword && promptReauth && activeAccountId !== undefined) {
      promptReauth(activeAccountId);
      return;
    }
    showArrivals();
  }, [activeAccountId, activeNeedsPassword, promptReauth, showArrivals]);

  /**
   * Catching up — after a gap, or on an arrival — without the jump
   * `refetchOnWindowFocus: false` exists to prevent.
   *
   * The rule that has always applied here is "an arrival must not move rows
   * under the cursor". What was wrong was reading it as "an arrival must never
   * move rows", which left a user staring at an unchanged list: measured
   * against the live build, a message appended to the Inbox of a **visible,
   * healthy** window put a pill on screen in 1.0 s and put the row in the list
   * in 409 s — and only then because the stream happened to drop and resync.
   * Without that accident it would never have appeared, which is precisely the
   * "I have to reload the page" that was reported.
   *
   * A refetch re-sorts the list. That is harmless for someone whose list is
   * parked at the top with nothing selected and nothing open, and it is exactly
   * the thing not to do to someone mid-triage, whose cursor, pointer and scroll
   * position are all load-bearing. So the same news produces two behaviours,
   * chosen by what the view is actually doing:
   *
   * - **Quiet**, when the list is parked: no selection, no open message, the
   *   keyboard cursor on the first row and the scroller at the top. Rows can
   *   only appear above a cursor that is already at the top, so nothing moves
   *   under anything.
   * - **Announced**, otherwise: the gap falls through to the pill below, which
   *   is the affordance this app already uses for "there is more, ask for it".
   *
   * Only while the document is visible: refetching a hidden tab spends a
   * request on a list nobody is reading, and the `wake` in `useLiveUpdates`
   * brings the stream back the moment they return anyway.
   */
  const listParked = !selecting && messageId === undefined && cursorIndex <= 0;

  const listFetching = listQuery.isFetching;
  /*
   * A stream gap in conversation mode is one more `sync`: it asks the server
   * what changed since the list's token, which is exactly the question a gap
   * leaves open — no refetch, no pill.
   */
  const requestConversationSync = conversationList.requestSync;
  useEffect(() => {
    if (!conversationMode || !streamGap) return;
    clearStreamGap();
    requestConversationSync();
  }, [conversationMode, requestConversationSync, streamGap]);
  useEffect(() => {
    if (conversationMode) return;
    if (!streamGap && newArrivals === 0) return;
    if (!listParked) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    // Scroll is read here rather than during render: it is a live DOM value,
    // not state, and a render that read it would be reading a ref it does not
    // re-render for.
    if ((gridRef.current?.scrollTop ?? 0) > CATCH_UP_SCROLL_EPSILON_PX) return;
    if (typeof window !== 'undefined' && window.scrollY > CATCH_UP_SCROLL_EPSILON_PX) return;
    // A fetch is already out — typically the one opening a folder whose list
    // an arrival marked stale. Restarting it would abort a request (and the
    // backend's IMAP connection with it) to send the same one again; wait for
    // it instead, and this runs again when it lands.
    if (listFetching) return;
    // It landed holding every parked message: nothing left to catch up on.
    if (
      !streamGap &&
      !unified &&
      activeAccountId !== undefined &&
      folderId !== undefined
    ) {
      const parked = arrivalIds(activeAccountId, folderId);
      const listed = new Set(messages.map((message) => message.id));
      if (parked.size > 0 && [...parked].every((id) => listed.has(id))) {
        clearArrivals(activeAccountId, folderId);
        return;
      }
    }
    showArrivals();
  }, [
    activeAccountId,
    conversationMode,
    folderId,
    listFetching,
    listParked,
    messages,
    newArrivals,
    showArrivals,
    streamGap,
    unified,
  ]);

  /*
   * Pull-to-refresh on handheld (chunk H, §5.4): the Refresh button's refetch,
   * which also brings in whatever the "N new" pill was holding back. Returns
   * the refetch so the indicator spins until it settles.
   */
  const pullRefresh = useCallback(() => {
    clearListArrivals();
    return listQuery.refetch();
  }, [clearListArrivals, listQuery]);

  /**
   * The handheld FAB's Compose (ADR 0048 §8). Through the URL, like the
   * shell's Compose, and *pushed*: the composer is then a place in the history
   * and the hardware back button closes it (the composer saves a dirty draft
   * as it unmounts).
   */
  const composeFromFab = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set(COMPOSE_PARAM, COMPOSE_INTENT_NEW);
    navigate(
      { search: params.toString() },
      { state: { composePushed: true } satisfies MailLocationState },
    );
  }, [navigate, searchParams]);

  /**
   * The drawer's middle section on mail: the folder tree (§3.2). The shell
   * draws the rest of the drawer. Memoised, because each new node identity is
   * a re-registration and a re-render of the shell.
   */
  const selectingEntry = locationState.selecting === true;
  const drawerPanel = useMemo(
    () => (
      <div className="drawer__section">
        <div className="drawer__section-title section-title">
          {accounts.length > 1 ? 'Mailboxes' : 'Folders'}
        </div>
        {/* The same tree the desktop sidebar draws: every mailbox, each
            opening onto its own folders, with "All inboxes" above them. */}
        <MailboxTree
          selectedFolderId={folderId}
          unifiedSelected={unified}
          onSelectUnified={() => navigate(UNIFIED_PATH, { replace: selectingEntry })}
          // Changing folder ends selection mode (the selection belongs to
          // this list); replacing its history entry keeps back from
          // returning to a selection that is no longer there.
          onSelectFolder={(target) =>
            navigate(`/mail/${encodeURIComponent(target.id)}`, { replace: selectingEntry })
          }
        />
      </div>
    ),
    [accounts.length, folderId, navigate, selectingEntry, unified],
  );
  useDrawerPanel(handheld ? drawerPanel : null);

  /*
   * Swipe (chunk F, §6.1): the same archive and remove the keymap calls. Off
   * in Trash and Junk, where neither verb has a sensible pair.
   */
  // The merged list is Inboxes only, where archive and delete both mean
  // something; in a folder view Trash and Junk still turn swiping off.
  // A cross-folder result list is not "in" the route's folder at all, so the
  // route's Trash or Junk says nothing about its rows.
  const swipeOff =
    !unified && !crossFolder && (folder?.role === 'trash' || folder?.role === 'junk');
  const swipe = useMemo(
    () => ({
      // Offered on every row now, and refused out loud (#343): a right swipe
      // that silently did nothing on a mailbox with no Archive folder looked
      // like a broken gesture. The row springs back and the snackbar says why.
      onArchive: (target: MessageSummary) => archive(target),
      onDelete: (target: MessageSummary) => remove(target),
      // Never while selecting: a swipe must not act on one row while several
      // are selected.
      enabled: handheld && !swipeOff && !selecting,
    }),
    [archive, handheld, remove, selecting, swipeOff],
  );

  const stackedView = messageId ? 'message' : 'list';
  // While a refetch is in flight the rows on screen are the honest state; an
  // error line next to them would be describing an attempt, not a result.
  const showListError = listQuery.isError && !listQuery.isFetching;

  /**
   * The list was never cached, and there is no connection to fetch it with.
   *
   * Not an error, and it must not be dressed as one: nothing failed. The
   * folder was simply never opened while there was a network, so there is no
   * copy of it on this device. The list's ordinary "Nothing here" empty state
   * would be a lie in the other direction — it says the folder is empty.
   *
   * Two ways to arrive, and both have to be read. A request that went out and
   * never came back is an `isOffline` `ApiError` — that is the browser still
   * believing it has a network. A browser that *knows* it has none never sends
   * the request at all and leaves the query paused with no error (see
   * `listPaused`); that is the ordinary case, and reading only the first one
   * is why this state was unreachable in exactly the situation it is for.
   */
  const notAvailableOffline =
    messages.length === 0 &&
    ((showListError && isOfflineError(listQuery.error)) || (listPaused && listQuery.isPending));

  /** "In Tasks" under the subject (ADR 0075), in either pane. */
  const taskChip =
    detailQuery.data && messageId !== undefined && detailQuery.data.id === messageId ? (
      <EmailTaskChip
        accountId={detailQuery.data.accountId || (paneAccountId ?? '')}
        messageId={detailQuery.data.id}
        headers={detailQuery.data.headers}
        onOpen={(task) =>
          navigate(
            taskReminderUrl(detailQuery.data?.accountId || (paneAccountId ?? ''), task.listId, task.id),
          )
        }
      />
    ) : undefined;

  /** The message reading pane: the flat list's. */
  const messagePane = (
    <ReadingPane
      accountId={paneAccountId ?? ''}
      onOpenContact={(contactId) => navigate(`/contacts/${encodeURIComponent(contactId)}`)}
      remoteMode={remoteMode}
      onRemoteModeChange={(mode) =>
        setRemoteChoice(
          mode === undefined || messageId === undefined ? undefined : { messageId, mode },
        )
      }
      isJunk={
        (paneAccountId !== undefined
          ? accountFolders.get(paneAccountId)?.byRole('junk')?.id
          : folderByRole('junk')?.id) === detailQuery.data?.folderId
      }
      remoteRules={remoteRulesQuery.data ?? []}
      message={detailQuery.data}
      aiSummary={aiSummary}
      isLoading={messageId !== undefined && detailQuery.isLoading}
      // A failed refresh of a body already on screen (messageDetailStaleTime)
      // is not a failure to show it.
      error={
        messageId !== undefined && (detailQuery.data === undefined || detailQuery.isPlaceholderData)
          ? (detailQuery.error ?? undefined)
          : undefined
      }
      onRetry={() => void detailQuery.refetch()}
      onBack={backToList}
      {...(openMessageRow && canArchive([openMessageRow])
        ? { onArchive: () => archive(openMessageRow) }
        : {})}
      onDelete={() => {
        if (openMessageRow) remove(openMessageRow);
      }}
      {...(detailQuery.data && spamVerbFor([currentMessage ?? detailQuery.data]) !== undefined
        ? {
            onSpam: () => {
              if (openMessageRow) spam(openMessageRow);
            },
          }
        : {})}
      {...(detailQuery.data &&
      detailQuery.data.from.email.trim() !== '' &&
      canBlockSender(openMessageRow ?? detailQuery.data)
        ? {
            onBlockSender: () => {
              const target = openMessageRow ?? detailQuery.data;
              if (target) askToBlock(target);
            },
          }
        : {})}
      // A `mailto:` unsubscribe is a message the user sends themselves:
      // the composer opens filled in, and nothing leaves until Send.
      onComposeUnsubscribe={(seed) => {
        // From the mailbox the list wrote to, as a body mailto: is (#343).
        pinComposeTo(paneAccountId);
        setCompose({ mode: 'new', to: seed.to, subject: seed.subject, body: seed.body });
      }}
      // The same optimistic mutation the list uses, so the unread counter
      // in the sidebar moves with it.
      onMarkRead={(message) =>
        setFlags.mutate({ messages: [message], update: { add: ['seen'] } })
      }
      onMarkUnread={() => {
        if (openMessageRow) setRead(openMessageRow, false);
      }}
      {...(openMessageRow ? { onMoveTo: () => openMoveToFor(openMessageRow) } : {})}
      onMailto={(href) => {
        pinComposeTo(paneAccountId);
        setCompose(mailtoSeed(href));
      }}
      onReply={openReply}
      forwardBusy={forwarding !== undefined && forwarding === messageId}
      actionProblem={
        forwardProblem !== undefined && forwardProblem.messageId === messageId
          ? forwardProblem.text
          : undefined
      }
      onToggleFlag={() => {
        if (openMessageRow) toggleFlag(openMessageRow);
      }}
      {...(openMessageRow ? { onAddToTasks: () => addToTasks([openMessageRow]) } : {})}
      {...(openMessageRow && canSnooze([openMessageRow]) ? { onSnooze: () => snooze([openMessageRow]) } : {})}
      {...(openMessageRow ? { onLabel: () => labelAs([openMessageRow]) } : {})}
      taskChip={taskChip}
      labelChips={
        detailQuery.data && detailQuery.data.id === messageId ? (
          <HeaderLabelChips
            labels={labelsForKeywords(
              detailQuery.data.labelKeywords,
              labelsByAccount.get(detailQuery.data.accountId || (paneAccountId ?? '')),
            )}
            onRemove={(label) => {
              const detail = detailQuery.data;
              if (detail) removeLabel(detail.accountId || (paneAccountId ?? ''), [detail.id], label);
            }}
          />
        ) : undefined
      }
    />
  );

  /*
   * The conversation pane (ADR 0077, plan/16 §8).
   *
   * In conversation mode an open message is shown as its conversation — the
   * stack `ConversationPane` draws. Every verb it offers is one of this view's
   * own, on the conversation as a row: the same functions the keys, the menu
   * and the list call, so a toolbar Archive and an `e` are one code path and
   * one Undo. A conversation not (yet) in the loaded list still works: the
   * detail names its members, and the row is rebuilt from it.
   */
  const paneTarget: MessageSummary | undefined = openMessageRow ?? detailQuery.data;
  const paneRow = (detail: { conversation: ConversationSummary }) =>
    conversationRowFor(detail.conversation);
  /** A verb that takes the conversation away also leaves it. */
  const leaving = (row: MessageSummary) => {
    if (!isOpen(row)) backToList();
  };
  const replyFromPane = (message: MessageDetail | ConversationMessage, mode: 'reply' | 'reply-all' | 'forward') => {
    if (mode === 'forward') {
      openForward(message);
      return;
    }
    const account = accounts.find((candidate) => candidate.id === message.accountId) ?? paneAccount;
    const own = account?.email ?? ownAddress;
    const aliases = account?.aliases ?? [];
    pinComposeTo(message.accountId || paneAccountId);
    // A body that is loaded is quoted; a collapsed one is answered by its
    // envelope, as the list's menu does.
    setCompose(
      'bodyText' in message
        ? replySeed(message, mode, own, aliases)
        : summaryReplySeed(message, mode, own, aliases),
    );
  };
  const conversationPane: ConversationPaneProps | undefined =
    conversationMode && messageId !== undefined && paneAccountId !== undefined
      ? {
          accountId: paneAccountId,
          messageId,
          onBack: backToList,
          ...(paneTarget === undefined || canArchive([paneTarget])
            ? {
                onArchive: (_members, detail) => {
                  const row = paneRow(detail);
                  archive(row);
                  leaving(row);
                },
              }
            : {}),
          onDelete: (_members, detail) => {
            const row = paneRow(detail);
            remove(row);
            leaving(row);
          },
          ...(paneTarget !== undefined && spamVerbFor([paneTarget]) !== undefined
            ? {
                onSpam: (_members, detail) => {
                  const row = paneRow(detail);
                  spam(row);
                  leaving(row);
                },
              }
            : {}),
          onMoveTo: (_members, detail) => openMoveToFor(paneRow(detail)),
          onToggleStar: (_members, star, detail) =>
            setFlags.mutate(
              expandConversations({
                messages: [paneRow(detail)],
                update: star ? { add: ['flagged'] } : { remove: ['flagged'] },
              }),
            ),
          onMarkUnread: (_members, detail) => setRead(paneRow(detail), false),
          onAddToTasks: (_members, detail) => addToTasks([paneRow(detail)]),
          ...(paneTarget === undefined || canSnooze([paneTarget])
            ? { onSnooze: (_members, detail) => snooze([paneRow(detail)]) }
            : {}),
          onLabel: (_members, detail) => labelAs([paneRow(detail)]),
          labels: labelsByAccount.get(paneAccountId),
          // From every message of the conversation, merged Sent included.
          onRemoveLabel: (label, detail) =>
            removeLabel(
              paneAccountId,
              [...new Set([...labelTargetIds([paneRow(detail)]), ...detail.messages.map((m) => m.id)])],
              label,
            ),
          onReply: replyFromPane,
          forwardingId: forwarding,
          actionProblem: forwardProblem,
          ...(paneTarget !== undefined && canBlockSender(paneTarget)
            ? { onBlockSender: (message: MessageDetail) => askToBlock(message) }
            : {}),
          // One request for the batch the pane marks read, like every verb.
          onMarkRead: (read) => setFlags.mutate({ messages: read, update: { add: ['seen'] } }),
          onMailto: (href) => {
            pinComposeTo(paneAccountId);
            setCompose(mailtoSeed(href));
          },
          // A `mailto:` unsubscribe is sent by the user, from the mailbox the
          // list wrote to -- this conversation's, never the active one's.
          onComposeUnsubscribe: (seed) => {
            pinComposeTo(paneAccountId);
            setCompose({ mode: 'new', to: seed.to, subject: seed.subject, body: seed.body });
          },
          onOpenContact: (contactId) => navigate(`/contacts/${encodeURIComponent(contactId)}`),
          isJunk:
            (accountFolders.get(paneAccountId)?.byRole('junk')?.id ?? folderByRole('junk')?.id) ===
            (paneTarget?.folderId ?? folderId),
          remoteRules: remoteRulesQuery.data ?? [],
          taskChip,
        }
      : undefined;

  return (
    <div
      className="shell__panes"
      data-stacked-view={stackedView}
      {...(readingPane !== undefined ? { 'data-reading-pane': readingPane } : {})}
    >
      {/*
        Mounting the snackbar is what turns archive, delete and move into
        deferred commits with a five-second Undo (ADR 0048 §6.1): while it is
        on screen the mutation waits; with none mounted, it commits at once.
      */}
      <UndoSnackbar />
      {/*
        The screen's h1 (#205): what is being listed. Outside both panes, so it
        is there whichever one a stacked layout (a phone, a narrow window, the
        reading pane "off") is showing; the open message's subject is the h2
        under it. The toolbar draws the same name for the eye.
      */}
      <h1 className="visually-hidden">
        {unified
          ? UNIFIED_LABEL
          : labelMode
            ? (currentLabel?.name ?? (labelMissing ? 'Label not found' : 'Loading…'))
            : crossFolder
              ? 'Search results'
              : (folder?.name ?? (foldersLoading ? 'Loading…' : 'Mail'))}
      </h1>
      <section className="pane pane--list" aria-label="Message list" ref={listPaneRef} data-pane="list">
        <PullIndicator
          targetRef={listPaneRef}
          onRefresh={pullRefresh}
          enabled={handheld && !messageId && !selecting}
        />
        <div className="list-toolbar">
          <span className="list-toolbar__title">
            {/* A search of several folders is not the route's folder: the route
                only follows the open result, so it is not named as the list. */}
            {unified ? (
              UNIFIED_LABEL
            ) : labelMode ? (
              currentLabel ? (
                <>
                  <LabelDot label={currentLabel} className="list-toolbar__label-dot" />
                  {currentLabel.name}
                </>
              ) : labelMissing ? (
                'Label not found'
              ) : (
                'Loading…'
              )
            ) : crossFolder ? (
              'Search results'
            ) : (
              (folder?.name ?? (foldersLoading ? 'Loading…' : 'Mail'))
            )}
          </span>
          {streamStatus && streamStatus.status !== 'connected' && (
            <span
              className="list-toolbar__status"
              data-status={streamStatus.status}
              title={streamStatus.reason ?? streamStatus.status}
            >
              {streamStatus.status === 'reauth-required'
                ? 'Sign in again'
                : streamStatus.status === 'connecting'
                  ? 'Connecting…'
                  : streamStatus.status === 'degraded'
                    ? 'Live updates degraded'
                    : 'Live updates offline'}
            </span>
          )}
          {folder && !crossFolder && (
            <span className="list-toolbar__count">
              {folder.unreadCount > 0 ? `${folder.unreadCount} unread` : `${folder.totalCount}`}
            </span>
          )}
          <span className="spacer" />
          {/* On handheld too, beside the pull-to-refresh gesture (§5.4): the
              gesture is touch-only, and this is its keyboard, switch and
              screen-reader twin. */}
          <button
            type="button"
            className="btn btn--icon"
            // Clears the "N new" pill too: the refetch brings that mail in, and
            // a pill left behind promised mail already on screen (#343).
            onClick={refresh}
            aria-label="Refresh"
            // New mail now announces itself (ADR 0002's stream, or the interim
            // poll of ADR 0021) and waits behind the "N new messages" pill, so
            // this is no longer the only way it appears — it is the way to ask
            // for everything else the server may have changed.
            title="Refresh this folder"
          >
            <Icon name="refresh" />
          </button>
          {/* The folder's own verbs (Mark all as read, Empty folder). Not in
              selection mode, where the actions on screen are the selection's
              and a second "Actions" beside them would be a way to act on the
              whole folder by mistake. */}
          {folder && !selecting && !crossFolder && <FolderActionsButton folder={folder} />}
        </div>

        {/*
          One mailbox of the merged list could not be read (ADR 0056 §3).
          It is named, the rest of the list below keeps working, and the one
          action offered is the one that can help — which for a mailbox whose
          password was rejected is the accounts page, not a retry the server
          has already stopped making.
        */}
        {unavailable.length > 0 && (
          <div
            className="list-unavailable"
            role="status"
            aria-label="Mailboxes that could not be loaded"
          >
            <span className="list-unavailable__text">
              {unavailable
                .map((problem) => `${mailboxName(problem.accountId, accounts)} ${problem.message}`)
                .join('; ')}
              . The other mailboxes are shown.
            </span>
            {unavailable.every((problem) => problem.reauth) ? (
              // One mailbox's password: ask for it here (#192). Several, or
              // a sign-in: the accounts page, where each has its row.
              unavailable.length === 1 && unavailable[0] && promptReauth && needsOwnPassword(unavailable[0].accountId) ? (
                <button
                  type="button"
                  className="btn btn--ghost"
                  aria-haspopup="dialog"
                  onClick={(event) => {
                    const accountId = unavailable[0]?.accountId;
                    if (accountId) promptReauth(accountId, event.currentTarget);
                  }}
                >
                  {accounts.find((candidate) => candidate.id === unavailable[0]?.accountId)?.auth === 'oauth2'
                    ? 'Sign in again'
                    : 'Re-enter password'}
                </button>
              ) : (
                <button type="button" className="btn btn--ghost" onClick={() => navigate('/settings/accounts')}>
                  Open accounts
                </button>
              )
            ) : (
              <button type="button" className="btn btn--ghost" onClick={reloadFolder}>
                Try again
              </button>
            )}
          </div>
        )}

        {searching && !labelMode && (
          <SearchScopeBar
            scope={scope}
            folderName={folder?.name ?? 'This folder'}
            includeTrashJunk={includeTrashJunk}
            overridden={hasInOperator(search)}
            onScopeChange={setScope}
            onIncludeTrashJunkChange={setIncludeTrashJunk}
            unified={unified}
          />
        )}

        {/*
          A cross-folder search that could not read every folder in time. The
          rows below are real; the sentence says which folders are not in
          them, and Retry runs the whole search again from its first page.
        */}
        {searchPartial && (
          <div className="list-search-gaps" role="status" aria-label="Folders that were not searched">
            <span className="list-search-gaps__text">
              {describeSearchGaps(searchGaps, (gap) =>
                unified
                  ? `${folderNameOf(gap.accountId, gap.folderId) ?? 'a folder'} (${mailboxName(gap.accountId, accounts)})`
                  : (folderNameOf(gap.accountId, gap.folderId) ?? 'a folder'),
              )}
            </span>
            <button type="button" className="btn btn--ghost" onClick={reloadFolder}>
              Retry
            </button>
          </div>
        )}

        {/* An automatic reply is answering mail in the owner's name: say so
            while it is on, with the one-click way to stop it. */}
        <VacationBanner accounts={laneAccounts} />

        {/* Sent, but the copy is not in Sent yet (#84): every mailbox, since a
            copy missing from one not on screen is still missing. */}
        <SentCopiesBanner accounts={accounts} />

        {/* The pill below is silent to a screen reader when it appears; this
            says it (#205). Always mounted, so the change is announced, and
            polite: new mail never interrupts what is being read. */}
        <span className="visually-hidden" role="status">
          {newArrivals > 0 ? `${newArrivals} new ${newArrivals === 1 ? 'message' : 'messages'}` : ''}
        </span>
        {(newArrivals > 0 || (streamGap && !conversationMode)) && (
          <div className="list-arrivals">
            {/*
              One affordance, two reasons. An arrival knows how many messages
              it is holding back; a stream gap knows only that this list may
              have fallen behind while the connection was down. Both are "there
              is more, ask for it", and both are made good by the same refetch.
            */}
            <button type="button" className="chip chip--action" onClick={showArrivals}>
              {newArrivals > 0
                ? `${newArrivals} new ${newArrivals === 1 ? 'message' : 'messages'}`
                : 'Show new mail'}
            </button>
          </div>
        )}

        {/* On desktop the selection bar stands where the chips were (doc 06 §5);
            on handheld it is portalled over the app bar. */}
        {selecting && (
          <SelectionBar
            variant={handheld ? 'appbar' : 'inline'}
            count={selectedMessages.length}
            allLoaded={selectedMessages.length === messages.length}
            hasMore={hasMore}
            onCancel={exitSelection}
            onSelectAll={selectAllLoaded}
            onActions={
              handheld
                ? undefined
                : (anchor) => setBulkMenu({ anchor, returnFocusTo: gridRef.current })
            }
          />
        )}
        <div className="list-filters" hidden={selecting && !handheld}>
          {(['all', 'unread', 'flagged'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className="chip"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {value === 'all' ? 'All' : value === 'unread' ? 'Unread' : 'Starred'}
            </button>
          ))}
          {/*
            The list's own connection state, next to the mail it describes, and
            the last-synced line with it — the single most reassuring thing an
            offline mail client can say. The shell's indicator reads the same
            source (`useConnectionStatus`), so the two cannot disagree.
          */}
          <ConnectionStatusLine syncedAt={listQuery.dataUpdatedAt} />
        </div>

        {notAvailableOffline ? (
          <NotAvailableOffline what="folder" onRetry={reloadFolder} />
        ) : (
          <MessageList
            messages={messages}
            selectedId={openRowKey}
            cursorIndex={cursorIndex}
            isLoading={listLoading}
            hasMore={hasMore}
            gridRef={gridRef}
            inactive={(handheld || readingPane === 'off') && messageId !== undefined}
            swipe={swipe}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            onSelectRange={selectRange}
            onSelect={openMessage}
            onCursorChange={setCursorIndex}
            onToggleFlag={toggleFlag}
            onContextMenu={openMenu}
            onOpenInWindow={openInWindow}
            dragRows={dragRows}
            {...(accountChips ? { accountChips } : {})}
            {...(folderLabelOf ? { folderLabelOf } : {})}
            labelsOf={labelsOf}
            {...(labelMode
              ? { emptyNote: 'No messages have this label yet. Press l on a message, or use Label as…, to add it.' }
              : {})}
            {...(crossFolder
              ? {
                  searchScopeNote: labelMode
                    ? 'This label was looked for in every folder except Trash and Junk'
                    : inOperator
                    ? 'This searched the folders your in: terms named'
                    : includeTrashJunk
                      ? 'This searched every folder, Trash and Junk included'
                      : 'This searched every folder except Trash and Junk',
                }
              : {})}
            searchQuery={debouncedSearch}
            sort={listSort}
            {...(sortsAvailable ? { sortsAvailable } : {})}
            onSortChange={setSort}
            onLoadMore={() => {
              if (listQuery.hasNextPage && !listQuery.isFetchingNextPage) void listQuery.fetchNextPage();
            }}
          />
        )}

        {/*
          A forward refused for a row that is not open in the reading pane
          (the menu reaches any row) has no message on screen to be said on,
          so it is said under the list instead.
        */}
        {forwardProblem !== undefined && forwardProblem.messageId !== messageId && (
          <div className="list-footer" role="alert">
            <p className="muted">{forwardProblem.text}</p>
          </div>
        )}

        {labelMissing && (
          <div className="list-footer" role="alert">
            <p className="muted">This label no longer exists in this mailbox.</p>
          </div>
        )}

        {showListError && !notAvailableOffline && (
          <div className="list-footer" role="alert">
            <p className="muted">{listErrorCopy(listQuery.error)}</p>
            <button type="button" className="btn" onClick={reloadFolder}>
              Try again
            </button>
          </div>
        )}
      </section>

      {/* tabIndex -1: where F6 and a stacked-layout open put focus (#205),
          without adding a tab stop. */}
      <section
        className="pane pane--reading"
        aria-label="Reading pane"
        ref={readingPaneRef}
        data-pane="message"
        tabIndex={-1}
      >
        {conversationPane ? <ConversationPane {...conversationPane} /> : messagePane}
      </section>

      {/*
        One implementation per verb, three ways in (key, menu, toolbar): every
        callback below is the same function the keymap calls.
      */}
      {menu && (
        <MessageContextMenu
          message={menu.message}
          folders={foldersOf(menu.message.accountId)}
          anchor={menu.anchor}
          openMoveTo={menu.openMoveTo}
          returnFocusTo={menu.returnFocusTo}
          variant={handheld ? 'sheet' : 'popover'}
          mailboxes={pickerMailboxes}
          online={online}
          onClose={() => setMenu(undefined)}
          actions={{
            open: () => openMessage(menu.message, messages.indexOf(menu.message)),
            openInWindow: () => openInWindow(menu.message, messages.indexOf(menu.message)),
            toggleRead: () => setRead(menu.message, !menu.message.flags.includes('seen')),
            toggleStar: () => toggleFlag(menu.message),
            archive: () => archive(menu.message),
            move: (folderId: string) => moveTo(menu.message, folderId),
            remove: () => remove(menu.message),
            ...(spamVerbFor([menu.message]) === 'not-spam'
              ? { notSpam: () => spam(menu.message) }
              : spamVerbFor([menu.message]) === 'report'
                ? { reportSpam: () => spam(menu.message) }
                : {}),
            ...(canBlockSender(menu.message) ? { blockSender: () => askToBlock(menu.message) } : {}),
            addToTasks: () => addToTasks([menu.message]),
            ...(canSnooze([menu.message]) ? { snooze: () => snooze([menu.message]) } : {}),
            labelAs: () => labelAs([menu.message]),
            reply: () => replyToMessage(menu.message, 'reply'),
            replyAll: () => replyToMessage(menu.message, 'reply-all'),
            forward: () => openForward(menu.message),
            copyTo: (targetFolderId: string) => copyTo(menu.message, targetFolderId),
            transfer: (accountId: string, targetFolderId: string, mode: DropMode) => {
              moveOrTransfer([menu.message], accountId, targetFolderId, mode);
              if (mode === 'move' && isOpen(menu.message)) backToList();
            },
            downloadOriginal: () => void messageDownload.download(menu.message),
            alwaysNotify: () =>
              notifyRuleMutation.mutate({
                accountId: menu.message.accountId || (paneAccountId ?? ''),
                address: menu.message.from.email,
                action: 'notify',
              }),
            muteSender: () =>
              notifyRuleMutation.mutate({
                accountId: menu.message.accountId || (paneAccountId ?? ''),
                address: menu.message.from.email,
                action: 'mute',
              }),
            clearNotifyRule: (ruleId: string) =>
              clearNotifyRuleMutation.mutate({
                accountId: menu.message.accountId || (paneAccountId ?? ''),
                ruleId,
              }),
          }}
          notifyRule={notifyRuleFor(menu.message.from.email)}
        />
      )}

      {bulkMenu && selecting && (
        <Menu
          label={`Actions for ${selectedMessages.length} selected messages`}
          header={`${selectedMessages.length} selected`}
          anchor={bulkMenu.anchor}
          {...(bulkMenu.openMoveTo ? { openSubmenuId: MOVE_TO_SUBMENU_ID } : {})}
          returnFocusTo={bulkMenu.returnFocusTo}
          variant={handheld ? 'sheet' : 'popover'}
          onClose={() => setBulkMenu(undefined)}
          // "The folder these are in" is the rows' own in a cross-folder
          // search — the route's folder is only where the search started.
          items={bulkMenuItems(
            selectedMessages,
            foldersOf(selectedMessages[0]?.accountId),
            crossFolder ? commonFolderOf(selectedMessages) : folderId,
            {
              ...bulk,
              ...(spamVerbFor(selectedMessages) === 'not-spam'
                ? { notSpam: bulk.spam }
                : spamVerbFor(selectedMessages) === 'report'
                  ? { reportSpam: bulk.spam }
                  : {}),
              addToTasks: () => addToTasks(selectedMessages),
              ...(canSnooze(selectedMessages) ? { snooze: () => snooze(selectedMessages) } : {}),
              labelAs: () => labelAs(selectedMessages),
              transfer: (accountId: string, targetFolderId: string, mode: DropMode) => {
                moveOrTransfer(selectedMessages, accountId, targetFolderId, mode);
                finishBulk(mode === 'move');
              },
              copy: (targetFolderId: string) => {
                const owner = selectedMessages[0]?.accountId || activeAccountId;
                if (owner === undefined) return;
                moveOrTransfer(selectedMessages, owner, targetFolderId, 'copy');
                finishBulk(false);
              },
              // One selected: the whole single-message menu is one step away,
              // which is how Reply, Forward and the Notify group stay reachable
              // on handheld now that long-press selects.
              moreActions: () => {
                const [only] = selectedMessages;
                if (!only) return;
                exitSelection();
                setMenu({
                  message: only,
                  anchor: bulkMenu.anchor,
                  openMoveTo: false,
                  returnFocusTo: gridRef.current,
                });
              },
            },
            { mailboxes: pickerMailboxes, online },
          )}
        />
      )}

      {labelTargets && labelTargets.length > 0 && (
        <LabelPicker
          accountId={labelTargets[0]!.accountId || (paneAccountId ?? activeAccountId ?? '')}
          targets={labelTargets.map((target) => target.labelKeywords ?? [])}
          subject={
            labelTargets.some(isConversationRow)
              ? labelTargets.length === 1
                ? 'this conversation'
                : `${labelTargets.length} conversations`
              : labelTargets.length === 1
                ? 'this message'
                : `${labelTargets.length} messages`
          }
          onClose={() => setLabelTargets(undefined)}
          onApply={({ add, remove }) => {
            applyLabels.apply({
              accountId: labelTargets[0]!.accountId || (paneAccountId ?? activeAccountId ?? ''),
              messageIds: labelTargetIds(labelTargets),
              add,
              remove,
            });
            // A selection that was labelled is done with, as after any bulk verb.
            if (selecting) exitSelection();
          }}
        />
      )}

      {taskTargets && (
        <AddToTaskDialog
          targets={taskTargets}
          handheld={handheld}
          onClose={() => setTaskTargets(undefined)}
          onSaved={(saved) => {
            emailTaskSnackbar.announce(saved);
            // A selection made into tasks is done with, as after any bulk verb.
            if (selecting) exitSelection();
          }}
        />
      )}
      {emailTaskSnackbar.snackbar}
      {/* Save to Nextcloud (ADR 0086): outlives the pane that started it. */}
      <SaveSnackbar />

      {snoozeTargets && (
        <SnoozeDialog
          targets={snoozeTargets.map((target) => ({
            accountId: target.accountId || (paneAccountId ?? activeAccountId ?? ''),
            messageId: target.id,
            subject: target.subject,
          }))}
          handheld={handheld}
          onClose={() => setSnoozeTargets(undefined)}
          onSnoozed={(done) => {
            invalidateSnoozed(queryClient, done.accountIds);
            schedulingSnackbar.announceSnoozed(done);
            // The open message was snoozed: it has left the folder the pane
            // says it is in, so back to the list, as after an archive.
            if (
              done.jobs.length > 0 &&
              openMessageRow !== undefined &&
              snoozeTargets.some(
                (target) =>
                  rowKey(target) === rowKey(openMessageRow) ||
                  (target.accountId === openMessageRow.accountId && target.id === messageId),
              )
            ) {
              backToList();
            }
            if (selecting) exitSelection();
          }}
          {...(snoozeTargets.length === 1
            ? {
                onRemindInstead: () => {
                  const target = snoozeTargets[0]!;
                  setSnoozeTargets(undefined);
                  addToTasks([target]);
                },
              }
            : {})}
        />
      )}
      {schedulingSnackbar.snackbar}

      {blockTarget && (
        <BlockSenderDialog
          accountId={blockTarget.accountId}
          address={blockTarget.address}
          folderId={blockTarget.folderId}
          onClose={() => setBlockTarget(undefined)}
          onBlocked={(address, movedExisting) => {
            showMailNotice(`${address} is blocked`);
            // The open message was one of those moved to Junk: it is no
            // longer in the folder the pane says it is in, so go back to the
            // list rather than show a message that has left.
            if (
              movedExisting &&
              currentMessage !== undefined &&
              messageId === currentMessage.id &&
              currentMessage.folderId === blockTarget.folderId &&
              currentMessage.from.email.trim().toLowerCase() === address.trim().toLowerCase()
            ) {
              backToList();
            }
          }}
        />
      )}

      {/*
        Mounted only while open, so every open starts from `initial` — no
        derived-state effect copying props into fields.
      */}
      {composeSeed && (
        <ComposeDialog
          open
          accountId={composeAccount?.id ?? paneAccountId}
          fromAddress={composeAccount?.email ?? ownAddress}
          initial={composeSeed}
          {...(composeProblem !== undefined ? { initialProblem: composeProblem } : {})}
          onClose={closeCompose}
          onPopOut={popOutCompose}
          onScheduled={(job, accountId) =>
            schedulingSnackbar.announceScheduled(job, accountId, {
              reopen: reopenScheduledDraft,
              view: () => navigate(SCHEDULED_PATH),
            })
          }
        />
      )}
      {/* Portalled into the shell's FAB slot; renders nothing outside the
          handheld list screen, where there is no slot. */}
      {/* Mid-selection Compose is no use, so the bottom-right button is the
          selection's: it opens the actions sheet. */}
      {handheld && messageId === undefined && !selecting && (
        <Fab key="compose" icon="compose" label="Compose" extended onClick={composeFromFab} />
      )}
      {handheld && messageId === undefined && selecting && (
        <Fab
          key="actions"
          icon="more"
          label="Actions"
          extended
          onClick={() =>
            setBulkMenu({ anchor: { x: 0, y: 0 }, returnFocusTo: gridRef.current })
          }
        />
      )}
      <ShortcutSheet
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        unavailable={{
          ...(currentMessage && !canArchive([currentMessage])
            ? { e: 'This mailbox has no Archive folder' }
            : {}),
          // ADR 0074: the key is bound only while the assistant is on.
          ...(aiAvailable ? {} : { 'shift+s': 'Turn on the AI assistant in Settings' }),
        }}
      />
      <span className="visually-hidden" aria-live="polite">
        {mutationFailed
          ? 'That change could not be saved and has been undone.'
          : (partialFailure ??
            windowNotice ??
            // Only changes a person made or needs to hear. Not "N loaded":
            // that was read out on every page fetch and every live update,
            // drowning the announcements that matter (doc 06 §11).
            (selecting ? `${selectedMessages.length} selected` : ''))}
      </span>
      {/* Navigation only (#205): the pane F6 landed in, "Opened: <subject>".
          Its own region, because the one above holds "N selected" for as long
          as a selection lasts and would hide these, or be evicted by them. */}
      <span className="visually-hidden" aria-live="polite">
        {paneFocus.announcement}
      </span>
    </div>
  );
}

/** A mailbox, as the "couldn't load" banner names it. */
function mailboxName(accountId: string, accounts: readonly { id: string; email: string }[]): string {
  return accounts.find((account) => account.id === accountId)?.email ?? 'A mailbox';
}

/**
 * What the list says when `GET /messages` failed.
 *
 * `503` is its own sentence on purpose. The backend answers 503 when its
 * datastore is down precisely so the frontend can tell that apart from a `401`;
 * presenting it as a sign-out — or silently signing the user out — is the exact
 * failure this distinction exists to prevent. The session layer owns the 401
 * path (the re-auth dialog), so the list does not restate it.
 */
/** A request that never reached the backend, offline or otherwise. */
function isOfflineError(error: unknown): boolean {
  return error instanceof ApiError && error.isOffline;
}

function listErrorCopy(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isFolderInvalidated) {
      return 'This folder changed on the server and its contents were reloaded.';
    }
    if (error.isServiceUnavailable) return 'Temporarily unavailable, retrying…';
    if (error.isOffline) return 'You’re offline. This folder will load when the connection is back.';
    if (error.isSessionExpired) return 'Waiting for you to sign in again.';
  }
  return 'Couldn’t load this folder.';
}

/**
 * Seeds the composer from a stored draft.
 *
 * Every field the composer holds comes back: the recipients (Bcc included, out
 * of the header the draft carries it in), both bodies, the attachments the
 * server re-staged, and the draft's own id — without which the next save would
 * append a second draft rather than replacing this one.
 */
export function seedFromDraft(draft: Draft): ComposeSeed {
  // One mapping, shared with the composer's "Load theirs" (#180): it carries
  // the draft's identity and version too, so the first save is conditional
  // on exactly what was opened (ADR 0100).
  return draftSeed(draft);
}

/**
 * Seeds the composer from the server's forward prefill.
 *
 * The body is the server's HTML (the forwarded-message block and the original,
 * already through the outgoing sanitizer), and the attachments arrive staged,
 * so the strip shows them as ready and the first autosave stores them.
 */
export function forwardSeed(prefill: ComposePrefill): ComposeSeed {
  return {
    mode: 'forward',
    to: formatAddressList(prefill.to),
    cc: formatAddressList(prefill.cc),
    subject: prefill.subject,
    bodyHtml: prefill.bodyHtml,
    body: prefill.bodyText ?? '',
    ...(prefill.forwardedMessageId ? { forwardedMessageId: prefill.forwardedMessageId } : {}),
    ...(prefill.attachments ? { attachments: prefill.attachments } : {}),
    // The server starts a forward at the alias the original reached.
    ...(prefill.fromAddress ? { from: prefill.fromAddress } : {}),
  };
}

/**
 * What to say when a forward could not be prepared.
 *
 * The ceilings are named with their reasons because they are the ones the user
 * can do something about (forward it from a client without the limit, or ask
 * for the files another way); the server's own sentence never reaches the user.
 */
export function forwardFailureCopy(error: unknown): string {
  if (error instanceof ApiError) {
    const reason = error.details?.['reason'];
    const maxBytes = error.details?.['max_bytes'];
    const limit = typeof maxBytes === 'number' ? ` (the limit is ${formatBytes(maxBytes)})` : '';
    if (error.status === 413 || reason === 'total_too_large' || reason === 'file_too_large') {
      return `This message’s attachments are too large to forward${limit}.`;
    }
    if (reason === 'too_many_attachments') {
      return 'This message has more attachments than a forward can carry.';
    }
    if (reason === 'empty_file') {
      return 'One of this message’s attachments is empty, so it can’t be forwarded.';
    }
    if (error.status === 404) return 'That message is no longer there; it may have been moved or deleted.';
    if (error.isOffline) return 'You’re offline. Forward this message once the connection is back.';
    if (error.isServiceUnavailable) return 'Forwarding is temporarily unavailable. Try again in a moment.';
  }
  return 'That message couldn’t be forwarded.';
}

/** Addresses as the composer's recipient fields spell them. */
function formatAddressList(addresses: readonly { name?: string; email: string }[]): string {
  return addresses
    .map((address) => (address.name ? `${address.name} <${address.email}>` : address.email))
    .join(', ');
}

/**
 * Seeds the compose dialog from an open message (doc 11 §9.5).
 *
 * Reply-all drops every one of the user's own addresses — the mailbox and its
 * aliases, case-insensitively — otherwise every reply-all CCs the sender to
 * themselves, and `Re:` is not stacked. The From starts at the alias the
 * message was sent to, if it was sent to one (ADR 0099).
 */
export function replySeed(
  message: MessageDetail,
  mode: 'reply' | 'reply-all',
  ownAddress: string | undefined,
  aliases: readonly string[] = [],
): ComposeSeed {
  const mine = new Set(ownAddresses(ownAddress, aliases).map((address) => address.toLowerCase()));
  const notMine = (address: { email: string }) => !mine.has(address.email.toLowerCase());
  const from = deliveredTo(message, ownAddress, aliases);

  const primary = message.replyTo[0] ?? message.from;
  const to = [primary, ...(mode === 'reply-all' ? message.to.filter(notMine) : [])];
  const cc = mode === 'reply-all' ? message.cc.filter(notMine) : [];

  const subject = /^re:/i.test(message.subject) ? message.subject : `Re: ${message.subject}`;
  // The quote is HTML from the start (#181): a real blockquote the composer
  // folds and the recipient's client can fold too, with the text part derived
  // from it like any other body. The empty paragraph above it is where the
  // caret starts.
  const attribution = `On ${formatFullDate(message.date)}, ${displayName(message.from)} wrote:`;
  const bodyHtml = `<p><br></p>${buildReplyQuote(attribution, message.bodyHtml, message.bodyText)}`;

  return {
    mode,
    to: dedupeAddresses(to).join(', '),
    cc: dedupeAddresses(cc).join(', '),
    subject,
    bodyHtml,
    body: htmlToText(bodyHtml),
    inReplyToMessageId: message.id,
    ...(from ? { from } : {}),
  };
}

/**
 * Seeds a reply from a *list row* — the context menu can act on a message whose
 * body was never fetched.
 *
 * No quoted body and no attribution: inventing either from a snippet would put
 * words in the sender's mouth. The addresses and the subject are the parts a
 * summary genuinely knows.
 */
export function summaryReplySeed(
  message: MessageSummary,
  mode: 'reply' | 'reply-all',
  ownAddress: string | undefined,
  aliases: readonly string[] = [],
): ComposeSeed {
  const mine = new Set(ownAddresses(ownAddress, aliases).map((address) => address.toLowerCase()));
  const notMine = (address: { email: string }) => !mine.has(address.email.toLowerCase());
  const to = [message.from, ...(mode === 'reply-all' ? message.to.filter(notMine) : [])];
  const from = deliveredTo(message, ownAddress, aliases);
  return {
    mode,
    to: dedupeAddresses(to).join(', '),
    cc: '',
    subject: /^re:/i.test(message.subject) ? message.subject : `Re: ${message.subject}`,
    body: '',
    inReplyToMessageId: message.id,
    ...(from ? { from } : {}),
  };
}

function dedupeAddresses(addresses: { name?: string; email: string }[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const address of addresses) {
    const key = address.email.toLowerCase();
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    out.push(address.name ? `${address.name} <${address.email}>` : address.email);
  }
  return out;
}
